/**
 * THE WORDS OF THE BILLING COMPONENTS — the plan, the credits, the limits, the
 * invoices, the consumption, and the installation's side of all five.
 *
 * English is the ORIGIN, pt-BR the translation — the same rule and the same
 * shape as `catalog.ts`, which was the seed of i18n in this product.
 *
 * ── WHY ONE FILE FOR SIX SUBJECTS ─────────────────────────────────────────
 *
 * Because a component may mount exactly ONE local scope: `useI18n({ messages })`
 * called twice in the same setup returns the FIRST composer and silently ignores
 * the second bag (vue-i18n warns `DUPLICATE_USE_I18N_CALLING` and moves on). A
 * dictionary per screen would therefore be unreachable the moment a screen draws
 * two of them — and `RailBadge`, `RESOURCE_*` and the invoice status words are
 * drawn by BOTH the customer's screens and the installation's console. One bag,
 * namespaced by subject, is what those two facts add up to.
 *
 * The dictionaries beside this one stay where they are: they belong to
 * screens that mount them alone (`subscribe.ts` on the entry wall, `catalog.ts`
 * on the platform catalogue, `ai.ts` on the AI panel).
 *
 * ── WHAT IS NOT HERE ──────────────────────────────────────────────────────
 *
 * `action.*` and `state.*` — "Cancel", "Close", "Loading…". Those live in
 * `shared/i18n/common.ts`, are mounted in the ROOT composer, and a local scope
 * falls back to the root for a key it does not have. A screen writes
 * `t("action.cancel")` and gets it, without importing anything. Copying them here
 * would be the fortieth translation of "Cancel".
 *
 * Money, counts, dates and durations: `@/shared/format` via `../api/money`. No
 * sentence in this file writes a currency symbol or a thousands separator —
 * `Intl` knows how each language writes them and we do not.
 *
 * ── THE SENTENCES THAT ARE THE PRODUCT ────────────────────────────────────
 *
 * Price before the click, pro-rata before confirming, the consequence of turning
 * something off, and what did NOT happen when a request failed ("nothing was
 * charged", "the balance was not changed"). On a screen about money that last
 * distinction is the whole message: a translation that shortened it to "something
 * went wrong" would turn a network blip into a suspicion about the bill.
 */
export const billingMessages = {
	en: {
		waiver: {
			until: "Plan complimentary until {date}",
			untilSetupHint:
				"Set up payment before {date} to keep your plan. Nothing is charged until then — the first charge is on {date}.",
			untilConfigured: "Payment set up. The first charge is on {date}.",
			label: "Waive this plan's subscription fee",
			hint: "Keeps the selected plan and its limits. New purchases are charged separately.",
			active: "Plan subscription fee waived",
			setup: "Set up payment",
			setupHint:
				"Your plan remains complimentary. Add a payment method in Stripe to buy extras or credits.",
			failed: "Could not open payment setup.",
			free: "Free plan",
			freeSetupHint:
				"No card needed to use it. Add one when you want to buy credits or add-ons — nothing is charged now.",
		},
		/** The bar of five sections, drawn by every billing screen. */
		nav: {
			aria: "Billing sections",
			plan: "My plan",
			credits: "Credits",
			limits: "Limits",
			invoices: "Invoices",
			consumption: "Consumption",
		},

		/**
		 * The four billable resources. `hint` is the one line under the card's
		 * title that says what the resource IS — a limit nobody can define is a
		 * limit nobody can plan against.
		 */
		resource: {
			users: {
				label: "users",
				hint: "people with access to this workspace",
				amount: "{n} user | {n} users",
				quantity: "{n}",
			},
			channels: {
				label: "channels",
				hint: "numbers, inboxes and chats connected",
				amount: "{n} channel | {n} channels",
				quantity: "{n}",
			},
			storage_gb: {
				label: "storage",
				hint: "files, media and attachments kept",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "active contacts",
				hint: "distinct people served in the month",
				amount: "{n} active contact | {n} active contacts",
				quantity: "{n}",
			},
		},

		/**
		 * The rail, and what being on it means. A workspace with no subscription is
		 * a legitimate answer and not an error: a new workspace browses the whole
		 * product before choosing a plan.
		 */
		rail: {
			none: "no billing",
			gateway: { label: "card", hint: "charged automatically to the card" },
			free: { label: "free", hint: "not charged" },
			courtesy: { label: "courtesy", hint: "not charged" },
			trial: { label: "trial", hint: "in the free trial period — no charge yet" },
		},

		/**
		 * A status the server may invent tomorrow is drawn RAW rather than folded
		 * into one of the words below — which is why the lookups miss instead of
		 * defaulting.
		 */
		subscriptionStatus: {
			active: "active",
			trialing: "in trial",
			past_due: "past due",
			canceled: "canceled",
			incomplete: "incomplete",
			expired: "expired",
		},
		invoiceStatus: { open: "pending", paid: "paid", overdue: "overdue", void: "void" },
		ledgerKind: {
			consumption: "consumption",
			debit: "consumption",
			refill: "cycle renewal",
			plan_change: "plan change",
			trial_end: "trial ended",
			plan_end: "plan ended",
			bonus: "bonus",
			pack: "pack bought",
			adjustment: "adjustment",
			reversal: "reversal",
			expiry: "expiry",
		},

		plan: {
			/** One plan on the choosing grid. The verb IS the comparison with today's. */
			card: {
				current: "Current plan",
				select: "Select",
				upgrade: "Upgrade",
				downgrade: "Downgrade",
				switch: "Switch",
				featured: "featured",
				perMonth: "/month",
				creditsPerMonth: "{credits} credits per month",
				smartAnswers: "≈ {n} answers",
				smartAnswersRecent: "≈ {n} answers at your recent usage",
				noLimit: "no limit",
				users: "users",
				channels: "channels",
				storage: "storage",
				contacts: "contacts/mo",
				units: "workspaces",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "Could not read the plan catalog",
				errorHint: "That is a failure on our side, not a lack of plans. Try again shortly.",
				emptyTitle: "No plan published",
				emptyHint: "This installation has not published a commercial catalog yet.",
				failed: "Could not switch the plan now. Nothing was charged.",
				noGateway:
					"Paid plans are charged by card, and card payments are not switched on in this installation — a paid plan cannot be chosen here until they are.",
			},
			/** Today's plan, decomposed — the screen adds nothing up, the server does. */
			cycle: {
				title: "My plan",
				creditsPerMonth: "{credits} credits per month",
				trialCredits: "{credits} credits to try it out",
				monthly: "plan",
				extras: "add-ons",
				total: "total per month",
				nextCharge: "next charge",
				noNextCharge: "none scheduled",
				trialEnds: "trial ends",
				trialDaysLeft: "ends today | {n} day left | {n} days left",
				yearly:
					"Yearly plan: {price} per year, renews on {date}. Credits still refill every month.",
				anchor: "every month on day {day}",
				window: "Cycle from {from} to {to}, {anchor} — {rail}.",
				trialWindow: "Free trial until {date}: nothing is charged until then.",
				noSubscription: "This plan has no subscription yet: nothing is charged automatically.",
				noPlan: "This workspace has no plan yet.",
				/** Ends on the conjunction: the link that finishes it is the next node. */
				noPlanHint:
					"You can use the whole panel without one — what you cannot do is call the AI. Choose a plan below and the credits arrive at that same instant, or",
				noPlanLink: "see the grid without the panel around it",
			},
			subscription: {
				title: "Subscription",
				scheduledTitle: "Subscription cancelled for the end of the cycle.",
				scheduledBody:
					"The plan runs until {date} and nothing is charged after that. The plan's credits stop renewing; the bonus balance does not expire.",
				reactivate: "Reactivate subscription",
				/**
				 * NO DEAD BUTTON: with the gateway off this screen offers no checkout,
				 * it says so. A button opening a checkout that does not exist is worse
				 * than its absence.
				 */
				noGateway:
					"Automatic payment is not switched on in this installation. Paid plans are charged by card only, so nothing is charged here until it is — under Invoices you still see every invoice already issued.",
				activate: "Switch monthly payment on",
				cancel: "Cancel subscription",
				cancelTitle: "Cancel the subscription?",
				cancelDescription:
					"The cancellation takes effect at the end of the cycle — nothing stops today.",
				cancelBody1:
					"The plan runs until {date}. After that the credit renewal stops and the AI stops answering; the panel, the conversations and the records stay reachable.",
				cancelBody2: "You can reactivate at any moment before that date, from this same screen.",
				keep: "Keep subscription",
				confirmCancel: "Cancel at the end of the cycle",
				cancelFailed: "Could not schedule the cancellation. Try again shortly.",
				reactivateFailed: "Could not reactivate the subscription.",
				checkoutFailed: "Could not open the payment now.",
			},
		},

		credits: {
			/**
			 * The balance in the top bar. Short on purpose: it is read sideways, a
			 * hundred times a day, by someone doing something else.
			 */
			chip: {
				credits: "{n} credits",
				detail: "{plan} from the plan and {bonus} in bonus.",
				renews: "Renews on {date}.",
				paused: "AI paused",
				pausedDetail:
					"Plan and bonus are at zero, so the AI stopped answering. A credit pack unblocks it at once.",
				blocked: "AI blocked",
				blockedDetail:
					"Blocked by whoever runs the installation. Buying credits does not undo it — talk to support.",
			},
			/** Two pockets, two meters — a single bar would hide "what do I lose at the turn?". */
			meters: {
				title: "Credits",
				blockedTitle: "AI blocked in this workspace.",
				blockedByAdmin:
					"The block was put in place by whoever runs the installation{reason}. Buying credits does NOT undo it — talk to support.",
				/** The reason, quoted, or nothing at all — never an empty pair of quotes. */
				reasonQuote: ": “{reason}”",
				blockedNoCredits:
					"The balance reached zero. A credit pack unblocks the answers at once, and the plan's renewal comes in by itself on {date}.",
				planLabel: "plan credits",
				percentUsed: "{percent}% of the cycle spent",
				renews: "renews on {date}",
				noRenewal: "no renewal date on record",
				bonus: "bonus",
				bonusHint: "does not expire",
				total: "total available",
				totalHint: "the plan is spent first",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "trial credits",
				trialUntil:
					"Usable until {date}. When you subscribe to a plan, whatever is left of the trial's welcome credits stays with you and adds to the plan's credits.",
			},
			packs: {
				title: "Buy credits",
				subtitle: "charged to your card now; the credits arrive right after",
				noPlan: "One-off packs are for whoever already has a plan.",
				noPlanHint:
					"A pack is charged on the month's invoice, and the invoice is born from the plan's cycle. Choose a plan first — its monthly credits may already solve it.",
				seePlans: "See the plans",
				empty: "This installation has not published one-off packs.",
				credits: "{credits} credits",
				perThousand: "{price} per 1,000 credits",
				buy: "Buy",
				bought: "{name} bought. The credits are already in the bonus balance.",
				failed: "Could not buy the pack. Nothing was charged.",
				confirmTitle: "Confirm the purchase",
				confirmDescription:
					"Your saved card is charged now. The credits arrive as soon as the charge goes through.",
				/** Opens on the dash because the pack's name is drawn in bold before it. */
				confirmBody:
					"— {credits} credits for {price}, charged to your saved card right now. They go to the bonus balance, which does not expire. If the card is declined, nothing is charged and no credits are added.",
				trialLocked: "Choose a plan to buy one-off credits.",
				cardLocked:
					"Packs are charged at once on the card of a paid plan, and this workspace has no card to charge.",
				notNow: "Not now",
			},
			/** What came IN, and why — the other half of "where did it go". */
			ledger: {
				title: "Credit entries",
				subtitle: "renewal, plan change, bonus, pack, adjustment, reversal",
				allKinds: "all kinds",
				includeConsumption: "Include consumption in the list",
				when: "when",
				kind: "kind",
				pool: "source",
				credits: "credits",
				planAfter: "plan balance after",
				bonusAfter: "bonus balance after",
				reason: "reason",
				welcome: "Welcome credits",
				welcomeTrial: "Trial welcome credits",
				poolPlan: "plan",
				poolBonus: "bonus",
				empty: "No credit entry in this period.",
			},
		},

		limits: {
			meter: "{resource} in use",
			fromPlan: "{n} from the plan",
			fromExtra: "+ {n} as add-on",
			waived: "+ {n} waived (not invoiced)",
			percentUsed: "{percent}% used",
			window: "Cycle window: {start} to {end}.",
			notEnforced:
				"This number is tracked and does not block anything in this version yet: going over the limit does not interrupt service.",
			enforcedContacts:
				"New contacts above the limit write in and their messages are kept, but nobody answers them until more contacts are bought or the cycle turns.",
			enforcedStorage:
				"Uploads by the team are refused while storage is full. Messages your customers send keep arriving and are kept.",
			scheduled: "Reduction to {quantity} scheduled for {date}.",
			addonField: "Add-on for {resource}",
			unitPrice: "{price} per unit/month · {total} with what is in the field",
			/**
			 * SOLD IN BLOCKS (0184): {price} buys {block} of the resource at once —
			 * R$ 29 for every 1,000 active contacts, R$ 29 for every 5 GB. The block
			 * is not decoration: {price} ÷ {block} is not a whole number of cents for
			 * contacts, so per-unit pricing cannot say what this costs.
			 */
			blockPrice: "{price} for every {block}/month · {total} with what is in the field",
			blockField: "Blocks of {block}",
			blockCeiling: "{n} block = +{units} on the ceiling | {n} blocks = +{units} on the ceiling",
			notWholeBlocks: "This add-on is sold in blocks of {block}. Ask for a whole number of them.",
			/**
			 * The pro-rata is ASYMMETRIC and that asymmetry is the one surprising
			 * thing on the screen: raising charges now for what is left of the cycle,
			 * lowering only takes effect at the turn. Written here, nobody finds out
			 * from the invoice.
			 */
			raise:
				"Raising from {from} to {to} charges now, pro-rata for what is left of the cycle, and takes effect at that same instant.",
			lowerAt:
				"Lowering from {from} to {to} takes effect on {date}. Until then the limit stays as it is today and the month's amount does not change.",
			lowerEndOfCycle:
				"Lowering from {from} to {to} takes effect at the end of the cycle. Until then the limit stays as it is today and the month's amount does not change.",
			notSold:
				"This add-on is not sold here. Talk to whoever runs the installation to raise the limit.",
			/**
			 * SEM TETO NENHUM, e por isso sem medidor e sem campo de adicional.
			 *
			 * `professional` e `scale` gravam `max_channels = 0`. Um "3 / 0 · 100%
			 * usado" seria a tela a inventar uma parede que não existe.
			 */
			unlimited: "Unlimited on this plan",
			unlimitedUsed: "{n} in use · no ceiling from the plan",
			/**
			 * A OFERTA — o que se diz a quem bateu no teto, e nunca um beco.
			 *
			 * `noneLead` não existe: a frase honesta de um plano pago que não vende
			 * aquele adicional é a `notSold` logo acima, e escrevê-la duas vezes é
			 * como duas telas passam a dizer a mesma coisa por palavras diferentes.
			 */
			offer: {
				title: "Plan limit reached",
				usage: "{used} of {limit} on the plan in use.",
				extrasLead: "Add-ons are sold in blocks, at your plan's price.",
				extrasAction: "Raise limit",
				planLead: "Add-ons come with a paid plan.",
				planAction: "Choose a plan",
				noneAction: "See plans",
			},
			monthly: "add-ons per month",
			discard: "Discard",
			save: "Save add-ons",
			saved:
				"Add-ons saved. The increase already applies; the reduction lands at the turn of the cycle.",
			failed: "Could not save the add-ons. Nothing was charged and nothing changed.",
		},

		invoice: {
			period: "{from} to {to}",
			paidOn: "Paid on {date}",
			dueOn: "Due on {date}",
			noDue: "No due date on record",
			cardPack: "Credit pack · {credits} credits · paid by card",
			receipt: "Receipt",
			receiptMissing: "The payment gateway returned no receipt for this charge.",
			lines: {
				description: "description",
				quantity: "qty.",
				unit: "unit",
				amount: "amount",
				subtotal: "subtotal",
			},
			/**
			 * The PATH matters more than the alarm: with no gateway there is no "pay
			 * now", and saying so is the service.
			 */
			pending: {
				overdueTitle: "Overdue invoice",
				openTitle: "Open invoice",
				head: "{title} — {amount}, {month}",
				dueOn: "Due on {date}.",
				noDue: "No due date on record.",
				gatewayOn: "The charge is attempted automatically on the card on file.",
				gatewayOff:
					"Automatic payment is not switched on here: this invoice is paid outside and marked as paid by whoever runs the installation.",
				see: "See the invoice",
			},
			/** A preview is not a document: the invoice is born when the cycle closes. */
			preview: {
				title: "Next invoice preview",
				empty: "Nothing to invoice in this cycle so far.",
				total: "expected total",
			},
		},

		consumption: {
			from: "from",
			to: "to",
			allSources: "all sources",
			rangeInverted: "The end date is before the start date. The list keeps the last valid period.",
			sourceName: {
				panel: "panel",
				channel: "channel",
				mission: "mission",
				automation: "automation",
			},
			calls: "answers",
			credits: "credits",
			avgDuration: "average duration",
			errors: "errors",
			when: "when",
			source: "source",
			agent: "agent",
			duration: "duration",
			state: "state",
			failed: "error",
			ok: "ok",
			failedDetail: "This answer failed.",
			turn: "see this answer in full",
			noDetail: "No detail recorded for this answer.",
			toolsTitle: "Tools charged in this turn",
			toolsLine: "{calls} × {rate} credits",
			toolsShare: "{n} credit on tools | {n} credits on tools",
			empty: "No AI answer in this period with these filters.",
		},

		/** The installation's side. None of these words is ever drawn for a customer. */
		admin: {
			courtesy: {
				title: "Courtesy",
				endsOn: "Ends at the renewal on {date}",
				endless: "No end date",
				card: "Card on file: the first full-price charge is on {date}.",
				noCard: "No card yet: if the customer does not subscribe, the workspace expires on {date}.",
				date: "Courtesy until",
				dateHint: "It ends at the renewal on or after this date.",
				save: "Save date",
				clear: "Remove end date",
				failed: "Could not save the courtesy end date.",
			},
			usage: {
				title: "Plan and usage",
				subtitle:
					"What the workspace uses against what its plan allows — the same numbers as its own limits screen.",
				plan: "Plan {plan} · {credits} credits a month",
				none: "This workspace has no plan.",
				unlimited: "unlimited",
			},
			margin: {
				title: "Cost against revenue, per workspace",
				subtitle:
					"What each workspace is charged, beside what its AI cost this house over the last 30 days.",
				workspace: "Workspace",
				plan: "Plan",
				charged: "Charged",
				credits: "Credits",
				aiCost: "AI cost (30d)",
				noPlan: "no plan",
				empty: "No workspace to compare yet.",
				note: "AI cost is not the only cost, and the monthly fee is not the only revenue — packs and extras are outside this table. The two columns are also in different currencies and are never added together: no exchange rate is kept here. Read a bad ratio as something to look into, not as a verdict.",
				error: "The comparison could not be loaded. Try reloading the page.",
			},
			kpis: {
				workspaces: "workspaces",
				onRail: "on the {rail} rail",
				invoiced: "invoiced in {month}",
				received: "received",
				open: "open",
				overdue: "overdue",
				note: "A healthy workspace is on exactly one rail — that is a primary key in the database, not a promise from this screen.",
				rule: "Invoiced = invoices issued this month + credit packs charged to the card this month. Received = what was paid this month, from any invoice. Open and overdue = everything unpaid right now. A declined charge, or one still waiting for 3-D Secure, does not count.",
			},
			table: {
				searchPlaceholder: "search workspace",
				allRails: "all rails",
				allPlans: "all plans",
				workspace: "workspace",
				plan: "plan",
				rail: "rail",
				nextCharge: "next charge",
				pending: "pending",
				extrasMonthly: "add-ons/mo",
				packs: "packs",
				monthlyTotal: "total/mo",
				billedMonth: "billed this month",
				cardPacks: "{count} by card",
				credits: "credits",
				closeCycle: "Close cycle",
				empty: "No workspace matches these filters.",
				/** Pressing twice is safe; the screen says which of the two happened. */
				alreadyClosed: "{name}'s cycle was already closed this month — nothing was charged again.",
				closed: "{name}'s cycle closed.",
				closedWithMonth: "{name}'s cycle closed ({month}).",
				closeFailed: "Could not close {name}'s cycle. Nothing was charged.",
				/** The server's own reason, kept: "not over yet" and "failed" ask for different things. */
				closeFailedWhy: "Could not close {name}'s cycle. {why} Nothing was charged.",
			},
			tenant: {
				title: "Billing",
				subtitle: "plan, credits, add-ons and the next invoice",
				none: "This workspace has no billing set up.",
				planCredits: "plan credits",
				bonus: "bonus",
				cost: "real cost of the cycle",
				costHint: "what the suppliers charged",
				margin: "margin",
				marginHint: "never shown to the customer",
				plan: "plan",
				noPlan: "no plan",
				switchNow: "Switch now",
				switchNote:
					"The switch takes effect at once: the new plan's credits arrive and the recurring amount changes.",
				invoices: {
					title: "Payment history",
					subtitle: "every invoice this workspace has received, and how each one ended",
					month: "month",
					total: "amount",
					status: "status",
					paidAt: "paid on",
					method: "method",
					externalRef: "external reference",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "card (Stripe)" },
					none: "No invoice has been issued for this workspace yet.",
				},
			},
			/** Adjusting and blocking are two facts with two remedies, so two controls. */
			creditActions: {
				title: "Credits, by hand",
				delta: "credits (±)",
				deltaPlaceholder: "500",
				pool: "pocket",
				poolPlan: "the plan's pocket (gone at the turn)",
				poolBonus: "bonus (does not expire)",
				reason: "reason",
				reasonPlaceholder: "stays in the ledger",
				post: "Post adjustment",
				needAmount: "Say how many credits, with a sign — negative debits.",
				needReason: "The reason stays in the ledger. It is required.",
				posted: "Adjustment posted to the ledger.",
				adjustFailed: "Could not post the adjustment. The balance did not change.",
				blockReason: "reason for the block",
				needBlockReason: "A block with no reason is a block nobody can explain later.",
				blocked: "AI blocked by a platform decision{reason}. Topping credits up does not undo it.",
				block: "Block AI",
				unblock: "Unblock AI",
				doneBlocked: "AI blocked for this workspace.",
				doneUnblocked: "Block removed.",
				blockFailed: "Could not change the block.",
			},
			extras: {
				resource: "resource",
				invoiced: "invoiced",
				waived: "waived",
				unit: "unit",
				subtotal: "subtotal",
				invoicedField: "Invoiced add-on for {resource}",
				waivedField: "Waived add-on for {resource}",
				scheduled: "{resource}: reduction to {quantity} scheduled for {date}.",
				cancelSchedule: "Cancel the schedule",
				save: "Save (charges pro-rata)",
				discard: "Discard",
				notForSale: "not for sale",
				perBlock: "per {size}",
				total: "add-ons total",
				failed: "Could not save the add-ons. Nothing was charged.",
			},
		},

		/**
		 * The words `Pagination` asks for by prop. `design/` knows no locale, so the
		 * CALL SITE supplies them — and a component may mount only one local scope,
		 * which is why they are here and not lifted from another feature's bag.
		 */
		pagination: {
			nav: "Pagination",
			of: "of",
			perPage: "Per page",
			previous: "Previous",
			next: "Next",
			page: "Page",
			goToPage: "Go to page",
			goTo: "Go to",
		},
	},

	"pt-BR": {
		waiver: {
			until: "Plano em cortesia até {date}",
			untilSetupHint:
				"Configure o pagamento antes de {date} para manter o plano. Nada é cobrado até lá — a primeira cobrança é em {date}.",
			untilConfigured: "Pagamento configurado. A primeira cobrança é em {date}.",
			label: "Abonar a mensalidade deste plano",
			hint: "Mantém o plano selecionado e seus limites. Compras novas são cobradas separadamente.",
			active: "Mensalidade do plano abonada",
			setup: "Configurar pagamento",
			setupHint:
				"Seu plano continua abonado. Cadastre uma forma de pagamento no Stripe para comprar adicionais ou créditos.",
			failed: "Não foi possível abrir a configuração de pagamento.",
			free: "Plano gratuito",
			freeSetupHint:
				"Use sem cartão. Cadastre um quando quiser comprar créditos ou adicionais — nada é cobrado agora.",
		},
		nav: {
			aria: "Seções de cobrança",
			plan: "Meu plano",
			credits: "Créditos",
			limits: "Limites",
			invoices: "Faturas",
			consumption: "Consumo",
		},

		resource: {
			users: {
				label: "usuários",
				hint: "pessoas com acesso a este workspace",
				amount: "{n} usuário | {n} usuários",
				quantity: "{n}",
			},
			channels: {
				label: "canais",
				hint: "números, caixas e chats conectados",
				amount: "{n} canal | {n} canais",
				quantity: "{n}",
			},
			storage_gb: {
				label: "armazenamento",
				hint: "arquivos, mídias e anexos guardados",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "contatos ativos",
				hint: "pessoas distintas atendidas no mês",
				amount: "{n} contato ativo | {n} contatos ativos",
				quantity: "{n}",
			},
		},

		rail: {
			none: "sem cobrança",
			gateway: { label: "cartão", hint: "cobrado automaticamente no cartão" },
			free: { label: "gratuito", hint: "não é cobrado" },
			courtesy: { label: "cortesia", hint: "não é cobrado" },
			trial: { label: "teste", hint: "no período de teste grátis — ainda sem cobrança" },
		},

		subscriptionStatus: {
			active: "ativa",
			trialing: "em teste",
			past_due: "em atraso",
			canceled: "cancelada",
			incomplete: "incompleta",
			expired: "vencida",
		},
		invoiceStatus: { open: "pendente", paid: "paga", overdue: "vencida", void: "anulada" },
		ledgerKind: {
			consumption: "consumo",
			debit: "consumo",
			refill: "renovação do ciclo",
			plan_change: "mudança de plano",
			trial_end: "fim do teste",
			plan_end: "fim do plano",
			bonus: "bônus",
			pack: "pacote comprado",
			adjustment: "ajuste",
			reversal: "estorno",
			expiry: "expiração",
		},

		plan: {
			card: {
				current: "Plano atual",
				select: "Selecionar",
				upgrade: "Subir de plano",
				downgrade: "Fazer downgrade",
				switch: "Trocar",
				featured: "destaque",
				perMonth: "/mês",
				creditsPerMonth: "{credits} créditos por mês",
				smartAnswers: "≈ {n} respostas",
				smartAnswersRecent: "≈ {n} respostas, pelo seu uso recente",
				noLimit: "sem limite",
				users: "usuários",
				channels: "canais",
				storage: "armazenamento",
				contacts: "contatos/mês",
				units: "workspaces",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "Não deu para ler o catálogo de planos",
				errorHint: "Isso é uma falha nossa, e não uma falta de planos. Tente de novo em instantes.",
				emptyTitle: "Nenhum plano publicado",
				emptyHint: "Esta instalação ainda não publicou um catálogo comercial.",
				failed: "Não deu para trocar o plano agora. Nada foi cobrado.",
				noGateway:
					"Planos pagos são cobrados no cartão, e o pagamento por cartão não está ligado nesta instalação — um plano pago não pode ser escolhido aqui até ele ser ligado.",
			},
			cycle: {
				title: "Meu plano",
				creditsPerMonth: "{credits} créditos por mês",
				trialCredits: "{credits} créditos para testar",
				monthly: "plano",
				extras: "adicionais",
				total: "total por mês",
				nextCharge: "próxima cobrança",
				noNextCharge: "nenhuma prevista",
				trialEnds: "fim do teste",
				trialDaysLeft: "termina hoje | falta {n} dia | faltam {n} dias",
				yearly:
					"Plano anual: {price} por ano, renova em {date}. Os créditos continuam a recarregar todo mês.",
				anchor: "todo dia {day}",
				window: "Ciclo de {from} a {to}, {anchor} — {rail}.",
				trialWindow: "Teste grátis até {date}: nada é cobrado até lá.",
				noSubscription: "Este plano ainda não tem uma assinatura: nada é cobrado automaticamente.",
				noPlan: "Este workspace ainda não tem plano.",
				noPlanHint:
					"Dá para usar o painel inteiro sem um — o que não dá é para invocar a IA. Escolha um plano abaixo e os créditos entram no mesmo instante, ou",
				noPlanLink: "veja a grade sem o painel em volta",
			},
			subscription: {
				title: "Assinatura",
				scheduledTitle: "Assinatura cancelada para o fim do ciclo.",
				scheduledBody:
					"O plano continua valendo até {date} e nada é cobrado depois disso. Os créditos do plano param de renovar; o saldo de bônus não expira.",
				reactivate: "Reativar assinatura",
				noGateway:
					"O pagamento automático não está ligado nesta instalação. Planos pagos são cobrados só no cartão, então nada é cobrado aqui até ele ser ligado — em Faturas você continua a ver todas as faturas já emitidas.",
				activate: "Ativar pagamento mensal",
				cancel: "Cancelar assinatura",
				cancelTitle: "Cancelar a assinatura?",
				cancelDescription: "O cancelamento vale no fim do ciclo — nada é interrompido hoje.",
				cancelBody1:
					"O plano continua até {date}. Depois disso a renovação de créditos para e a IA deixa de responder; o painel, as conversas e os registros continuam acessíveis.",
				cancelBody2: "Dá para reativar a qualquer momento antes dessa data, por esta mesma tela.",
				keep: "Manter assinatura",
				confirmCancel: "Cancelar no fim do ciclo",
				cancelFailed: "Não deu para agendar o cancelamento. Tente de novo em instantes.",
				reactivateFailed: "Não deu para reativar a assinatura.",
				checkoutFailed: "Não deu para abrir o pagamento agora.",
			},
		},

		credits: {
			chip: {
				credits: "{n} créditos",
				detail: "{plan} do plano e {bonus} de bônus.",
				renews: "Renova em {date}.",
				paused: "IA pausada",
				pausedDetail:
					"Plano e bônus zerados — a IA parou de responder. Um pacote de créditos destrava na hora.",
				blocked: "IA bloqueada",
				blockedDetail:
					"Bloqueio de quem administra a instalação. Comprar crédito não desfaz — fale com o suporte.",
			},
			meters: {
				title: "Créditos",
				blockedTitle: "IA bloqueada neste workspace.",
				blockedByAdmin:
					"O bloqueio foi posto por quem administra a instalação{reason}. Comprar créditos NÃO o desfaz — fale com o suporte.",
				reasonQuote: ": “{reason}”",
				blockedNoCredits:
					"O saldo chegou a zero. Um pacote de créditos volta a liberar as respostas na hora, e a renovação do plano entra sozinha em {date}.",
				planLabel: "créditos do plano",
				percentUsed: "{percent}% do ciclo consumido",
				renews: "renova em {date}",
				noRenewal: "sem data de renovação registrada",
				bonus: "bônus",
				bonusHint: "não expira",
				total: "total disponível",
				totalHint: "o plano é gasto primeiro",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "créditos de teste",
				trialUntil:
					"Valem até {date}. Ao assinar um plano, o que sobrar dos créditos de boas-vindas do teste continua com você e se soma aos créditos do plano.",
			},
			packs: {
				title: "Comprar créditos",
				subtitle: "cobrados no cartão agora; os créditos entram logo depois",
				noPlan: "Pacotes avulsos são para quem já tem um plano.",
				noPlanHint:
					"Um pacote é cobrado na fatura do mês, e a fatura nasce do ciclo do plano. Escolha um plano primeiro — os créditos mensais dele já podem resolver.",
				seePlans: "Ver os planos",
				empty: "Esta instalação não publicou pacotes avulsos.",
				credits: "{credits} créditos",
				perThousand: "{price} por 1.000 créditos",
				buy: "Comprar",
				bought: "{name} comprado. Os créditos já estão no saldo de bônus.",
				failed: "Não deu para comprar o pacote. Nada foi cobrado.",
				confirmTitle: "Confirmar a compra",
				confirmDescription:
					"O seu cartão salvo é cobrado agora. Os créditos entram assim que a cobrança passar.",
				confirmBody:
					"— {credits} créditos por {price}, cobrados agora no seu cartão salvo. Eles vão para o saldo de bônus, que não expira. Se o cartão for recusado, nada é cobrado e nenhum crédito entra.",
				trialLocked: "Escolha um plano para comprar créditos avulsos.",
				cardLocked:
					"Pacotes são cobrados na hora no cartão de um plano pago, e este workspace não tem cartão onde cobrar.",
				notNow: "Não agora",
			},
			ledger: {
				title: "Entradas de crédito",
				subtitle: "renovação, mudança de plano, bônus, pacote, ajuste, estorno",
				allKinds: "todos os tipos",
				includeConsumption: "Incluir o consumo na lista",
				when: "quando",
				kind: "tipo",
				pool: "origem",
				credits: "créditos",
				planAfter: "saldo do plano depois",
				bonusAfter: "saldo de bônus depois",
				reason: "motivo",
				welcome: "Créditos de boas-vindas",
				welcomeTrial: "Créditos de boas-vindas do teste",
				poolPlan: "plano",
				poolBonus: "bônus",
				empty: "Nenhuma entrada de crédito neste período.",
			},
		},

		limits: {
			meter: "{resource} em uso",
			fromPlan: "{n} do plano",
			fromExtra: "+ {n} de adicional",
			waived: "+ {n} de abono (não faturado)",
			percentUsed: "{percent}% usado",
			window: "Janela do ciclo: {start} a {end}.",
			notEnforced:
				"Este número é acompanhado e ainda não trava nada nesta versão: passar do limite não interrompe o atendimento.",
			enforcedContacts:
				"Contatos novos acima do limite escrevem e as mensagens ficam guardadas, mas ninguém os atende até comprar mais contatos ou virar o ciclo.",
			enforcedStorage:
				"Com o armazenamento cheio, os uploads da equipe são recusados. As mensagens que os clientes mandam continuam chegando e ficam guardadas.",
			scheduled: "Redução para {quantity} agendada para {date}.",
			addonField: "Adicional de {resource}",
			unitPrice: "{price} por unidade/mês · {total} com o que está no campo",
			blockPrice: "{price} a cada {block}/mês · {total} com o que está no campo",
			blockField: "Blocos de {block}",
			blockCeiling: "{n} bloco = +{units} no teto | {n} blocos = +{units} no teto",
			notWholeBlocks:
				"Este adicional é vendido em blocos de {block}. Peça um número inteiro deles.",
			raise:
				"Aumentar de {from} para {to} cobra agora, proporcional ao que resta do ciclo, e passa a valer no mesmo instante.",
			lowerAt:
				"Reduzir de {from} para {to} vale em {date}. Até lá o limite continua o de hoje e o valor do mês não muda.",
			lowerEndOfCycle:
				"Reduzir de {from} para {to} vale no fim do ciclo. Até lá o limite continua o de hoje e o valor do mês não muda.",
			notSold:
				"Este adicional não é vendido por aqui. Fale com quem administra a instalação para ampliar o limite.",
			unlimited: "Ilimitado neste plano",
			unlimitedUsed: "{n} em uso · sem teto do plano",
			offer: {
				title: "Limite do plano atingido",
				usage: "{used} de {limit} do plano em uso.",
				extrasLead: "Os adicionais são vendidos em blocos, ao preço do seu plano.",
				extrasAction: "Aumentar limite",
				planLead: "Adicionais entram com um plano pago.",
				planAction: "Escolher um plano",
				noneAction: "Ver planos",
			},
			monthly: "adicionais por mês",
			discard: "Descartar",
			save: "Salvar adicionais",
			saved: "Adicionais salvos. O aumento já vale; a redução entra na virada do ciclo.",
			failed: "Não deu para salvar os adicionais. Nada foi cobrado e nada mudou.",
		},

		invoice: {
			period: "{from} a {to}",
			paidOn: "Paga em {date}",
			dueOn: "Vence em {date}",
			noDue: "Sem vencimento registrado",
			cardPack: "Pacote de créditos · {credits} créditos · pago no cartão",
			receipt: "Recibo",
			receiptMissing: "O gateway de pagamento não devolveu recibo para esta cobrança.",
			lines: {
				description: "descrição",
				quantity: "qtd.",
				unit: "unitário",
				amount: "valor",
				subtotal: "subtotal",
			},
			pending: {
				overdueTitle: "Fatura vencida",
				openTitle: "Fatura em aberto",
				head: "{title} — {amount}, {month}",
				dueOn: "Vence em {date}.",
				noDue: "Sem data de vencimento registrada.",
				gatewayOn: "A cobrança é tentada automaticamente no cartão cadastrado.",
				gatewayOff:
					"O pagamento automático não está ligado aqui: esta fatura é paga por fora e marcada como paga por quem administra a instalação.",
				see: "Ver a fatura",
			},
			preview: {
				title: "Prévia da próxima fatura",
				empty: "Nada a faturar neste ciclo até agora.",
				total: "total previsto",
			},
		},

		consumption: {
			from: "de",
			to: "até",
			allSources: "todas as origens",
			rangeInverted:
				"A data final vem antes da inicial. A lista continua no último período válido.",
			sourceName: {
				panel: "painel",
				channel: "canal",
				mission: "missão",
				automation: "automação",
			},
			calls: "respostas",
			credits: "créditos",
			avgDuration: "duração média",
			errors: "erros",
			when: "quando",
			source: "origem",
			agent: "assistente",
			duration: "duração",
			state: "estado",
			failed: "erro",
			ok: "ok",
			failedDetail: "Esta resposta falhou.",
			turn: "ver esta resposta por inteiro",
			noDetail: "Sem detalhe registrado para esta resposta.",
			toolsTitle: "Ferramentas cobradas neste turno",
			toolsLine: "{calls} × {rate} créditos",
			toolsShare: "{n} crédito em ferramentas | {n} créditos em ferramentas",
			empty: "Nenhuma resposta da IA neste período com esses filtros.",
		},

		admin: {
			courtesy: {
				title: "Cortesia",
				endsOn: "Acaba na renovação de {date}",
				endless: "Sem data de fim",
				card: "Cartão cadastrado: a primeira cobrança a preço cheio é em {date}.",
				noCard: "Ainda sem cartão: se o cliente não assinar, o workspace vence em {date}.",
				date: "Cortesia até",
				dateHint: "Acaba na renovação que cair nesta data ou depois.",
				save: "Salvar data",
				clear: "Tirar data de fim",
				failed: "Não foi possível salvar a data de fim da cortesia.",
			},
			usage: {
				title: "Plano e uso",
				subtitle:
					"O que o workspace usa contra o que o plano dá — os mesmos números da tela de limites dele.",
				plan: "Plano {plan} · {credits} créditos por mês",
				none: "Este workspace não tem plano.",
				unlimited: "ilimitado",
			},
			margin: {
				title: "Custo contra receita, por workspace",
				subtitle:
					"O que cada workspace paga, ao lado do que a IA dele custou a esta casa nos últimos 30 dias.",
				workspace: "Workspace",
				plan: "Plano",
				charged: "Cobrado",
				credits: "Créditos",
				aiCost: "Custo de IA (30d)",
				noPlan: "sem plano",
				empty: "Nenhum workspace para comparar ainda.",
				note: "O custo de IA não é o único custo, e a mensalidade não é a única receita — pacotes e extras ficam de fora desta tabela. As duas colunas também estão em moedas diferentes e nunca são somadas: esta casa não guarda taxa de câmbio. Uma relação ruim é motivo para investigar, não um veredito.",
				error: "Não foi possível carregar a comparação. Recarregue a página.",
			},
			kpis: {
				workspaces: "workspaces",
				onRail: "no trilho {rail}",
				invoiced: "faturado em {month}",
				received: "recebido",
				open: "em aberto",
				overdue: "vencido",
				note: "Um workspace saudável está em exatamente um trilho — isso é chave primária no banco, e não uma promessa desta tela.",
				rule: "Faturado = faturas emitidas no mês + pacotes de créditos cobrados no cartão no mês. Recebido = o que foi pago no mês, de qualquer fatura. Em aberto e vencido = tudo o que está por pagar agora. Cobrança recusada ou à espera do 3-D Secure não conta.",
			},
			table: {
				searchPlaceholder: "buscar workspace",
				allRails: "todos os trilhos",
				allPlans: "todos os planos",
				workspace: "workspace",
				plan: "plano",
				rail: "trilho",
				nextCharge: "próxima cobrança",
				pending: "pendente",
				extrasMonthly: "adicionais/mês",
				packs: "pacotes",
				monthlyTotal: "total/mês",
				billedMonth: "faturado no mês",
				cardPacks: "{count} no cartão",
				credits: "créditos",
				closeCycle: "Fechar ciclo",
				empty: "Nenhum workspace com esses filtros.",
				alreadyClosed: "O ciclo de {name} já estava fechado neste mês — nada foi cobrado de novo.",
				closed: "Ciclo de {name} fechado.",
				closedWithMonth: "Ciclo de {name} fechado ({month}).",
				closeFailed: "Não deu para fechar o ciclo de {name}. Nada foi cobrado.",
				closeFailedWhy: "Não deu para fechar o ciclo de {name}. {why} Nada foi cobrado.",
			},
			tenant: {
				title: "Cobrança",
				subtitle: "plano, créditos, adicionais e a próxima fatura",
				none: "Este workspace não tem cobrança configurada.",
				planCredits: "créditos do plano",
				bonus: "bônus",
				cost: "custo real do ciclo",
				costHint: "o que os fornecedores cobraram",
				margin: "margem",
				marginHint: "nunca aparece para o cliente",
				plan: "plano",
				noPlan: "sem plano",
				switchNow: "Trocar agora",
				switchNote:
					"A troca vale no ato: os créditos do plano novo entram e o valor recorrente muda.",
				invoices: {
					title: "Histórico de pagamentos",
					subtitle: "todas as faturas deste workspace, e como cada uma terminou",
					month: "mês",
					total: "valor",
					status: "status",
					paidAt: "pago em",
					method: "forma",
					externalRef: "referência externa",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "cartão (Stripe)" },
					none: "Nenhuma fatura foi emitida para este workspace ainda.",
				},
			},
			creditActions: {
				title: "Créditos, à mão",
				delta: "créditos (±)",
				deltaPlaceholder: "500",
				pool: "bolso",
				poolPlan: "bolso do plano (some na virada)",
				poolBonus: "bônus (não expira)",
				reason: "motivo",
				reasonPlaceholder: "fica no razão",
				post: "Lançar ajuste",
				needAmount: "Informe quantos créditos, com sinal — negativo debita.",
				needReason: "O motivo fica no razão. Ele é obrigatório.",
				posted: "Ajuste lançado no razão.",
				adjustFailed: "Não deu para lançar o ajuste. O saldo não mudou.",
				blockReason: "motivo do bloqueio",
				needBlockReason:
					"Um bloqueio sem motivo é um bloqueio que ninguém consegue explicar depois.",
				blocked:
					"IA bloqueada por decisão da plataforma{reason}. Recarregar crédito não desfaz isto.",
				block: "Bloquear IA",
				unblock: "Desbloquear IA",
				doneBlocked: "IA bloqueada para este workspace.",
				doneUnblocked: "Bloqueio removido.",
				blockFailed: "Não deu para mudar o bloqueio.",
			},
			extras: {
				resource: "recurso",
				invoiced: "faturado",
				waived: "abono",
				unit: "unitário",
				subtotal: "subtotal",
				invoicedField: "Adicional faturado de {resource}",
				waivedField: "Abono de {resource}",
				scheduled: "{resource}: redução para {quantity} agendada para {date}.",
				cancelSchedule: "Cancelar agendamento",
				save: "Salvar (cobra proporcional)",
				discard: "Descartar",
				notForSale: "fora de venda",
				perBlock: "a cada {size}",
				total: "total de adicionais",
				failed: "Não deu para salvar os adicionais. Nada foi cobrado.",
			},
		},

		pagination: {
			nav: "Paginação",
			of: "de",
			perPage: "Por página",
			previous: "Anterior",
			next: "Próxima",
			page: "Página",
			goToPage: "Ir para a página",
			goTo: "Ir para",
		},
	},

	el: {
		waiver: {
			until: "Δωρεάν πρόγραμμα έως {date}",
			untilSetupHint:
				"Ορίστε τρόπο πληρωμής πριν από τις {date} για να διατηρήσετε το πρόγραμμα. Δεν χρεώνεται τίποτα μέχρι τότε — η πρώτη χρέωση γίνεται στις {date}.",
			untilConfigured: "Η πληρωμή ορίστηκε. Η πρώτη χρέωση γίνεται στις {date}.",
			label: "Απαλλαγή από τη συνδρομή αυτού του προγράμματος",
			hint: "Διατηρεί το επιλεγμένο πρόγραμμα και τα όριά του. Οι νέες αγορές χρεώνονται ξεχωριστά.",
			active: "Η συνδρομή του προγράμματος παρέχεται δωρεάν",
			setup: "Ρύθμιση πληρωμής",
			setupHint:
				"Το πρόγραμμά σας παραμένει δωρεάν. Προσθέστε τρόπο πληρωμής στο Stripe για να αγοράσετε πρόσθετα ή πιστώσεις.",
			failed: "Δεν ήταν δυνατό το άνοιγμα της ρύθμισης πληρωμής.",
			free: "Δωρεάν πλάνο",
			freeSetupHint:
				"Χρησιμοποιήστε το χωρίς κάρτα. Προσθέστε μία όταν θέλετε να αγοράσετε μονάδες ή πρόσθετα — δεν χρεώνεται τίποτα τώρα.",
		},
		nav: {
			aria: "Ενότητες χρέωσης",
			plan: "Το πλάνο μου",
			credits: "Πιστώσεις",
			limits: "Όρια",
			invoices: "Τιμολόγια",
			consumption: "Κατανάλωση",
		},

		resource: {
			users: {
				label: "χρήστες",
				hint: "άτομα με πρόσβαση σε αυτόν τον χώρο εργασίας",
				amount: "{n} χρήστης | {n} χρήστες",
				quantity: "{n}",
			},
			channels: {
				label: "κανάλια",
				hint: "αριθμοί, εισερχόμενα και chats συνδεδεμένα",
				amount: "{n} κανάλι | {n} κανάλια",
				quantity: "{n}",
			},
			storage_gb: {
				label: "αποθηκευτικός χώρος",
				hint: "αρχεία, πολυμέσα και συνημμένα που κρατιούνται",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "ενεργές επαφές",
				hint: "διακριτά άτομα που εξυπηρετήθηκαν τον μήνα",
				amount: "{n} ενεργή επαφή | {n} ενεργές επαφές",
				quantity: "{n}",
			},
		},

		rail: {
			none: "χωρίς χρέωση",
			gateway: { label: "κάρτα", hint: "χρεώνεται αυτόματα στην κάρτα" },
			free: { label: "δωρεάν", hint: "δεν χρεώνεται" },
			courtesy: { label: "παραχώρηση", hint: "δεν χρεώνεται" },
			trial: { label: "δοκιμή", hint: "σε δωρεάν δοκιμαστική περίοδο — καμία χρέωση ακόμη" },
		},

		subscriptionStatus: {
			active: "ενεργή",
			trialing: "σε δοκιμή",
			past_due: "ληξιπρόθεσμη",
			canceled: "ακυρωμένη",
			incomplete: "ατελής",
			expired: "έληξε",
		},
		invoiceStatus: {
			open: "σε εκκρεμότητα",
			paid: "πληρωμένο",
			overdue: "ληξιπρόθεσμο",
			void: "άκυρο",
		},
		ledgerKind: {
			consumption: "κατανάλωση",
			debit: "κατανάλωση",
			refill: "ανανέωση κύκλου",
			plan_change: "αλλαγή πλάνου",
			trial_end: "λήξη δοκιμής",
			plan_end: "λήξη πλάνου",
			bonus: "δώρο",
			pack: "αγορά πακέτου",
			adjustment: "προσαρμογή",
			reversal: "αντιλογισμός",
			expiry: "λήξη",
		},

		plan: {
			card: {
				current: "Τρέχον πλάνο",
				select: "Επιλογή",
				upgrade: "Αναβάθμιση",
				downgrade: "Υποβάθμιση",
				switch: "Αλλαγή",
				featured: "προτεινόμενο",
				perMonth: "/μήνα",
				creditsPerMonth: "{credits} πιστώσεις τον μήνα",
				smartAnswers: "≈ {n} απαντήσεις",
				smartAnswersRecent: "≈ {n} απαντήσεις, με βάση την πρόσφατη χρήση",
				noLimit: "χωρίς όριο",
				users: "χρήστες",
				channels: "κανάλια",
				storage: "αποθηκευτικός χώρος",
				contacts: "επαφές/μήνα",
				units: "χώροι εργασίας",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "Δεν ήταν δυνατή η ανάγνωση του καταλόγου πλάνων",
				errorHint: "Αυτό είναι δικό μας σφάλμα, όχι έλλειψη πλάνων. Δοκίμασε ξανά σε λίγο.",
				emptyTitle: "Κανένα δημοσιευμένο πλάνο",
				emptyHint: "Αυτή η εγκατάσταση δεν έχει δημοσιεύσει ακόμη εμπορικό κατάλογο.",
				failed: "Δεν ήταν δυνατή η αλλαγή πλάνου τώρα. Δεν χρεώθηκε τίποτα.",
				noGateway:
					"Τα πληρωμένα πλάνα χρεώνονται στην κάρτα, και οι πληρωμές με κάρτα δεν είναι ενεργές σε αυτή την εγκατάσταση — δεν μπορείς να διαλέξεις πληρωμένο πλάνο εδώ μέχρι να ενεργοποιηθούν.",
			},
			cycle: {
				title: "Το πλάνο μου",
				creditsPerMonth: "{credits} πιστώσεις τον μήνα",
				trialCredits: "{credits} πιστώσεις για δοκιμή",
				monthly: "πλάνο",
				extras: "πρόσθετα",
				total: "σύνολο ανά μήνα",
				nextCharge: "επόμενη χρέωση",
				noNextCharge: "καμία προγραμματισμένη",
				trialEnds: "λήξη δοκιμής",
				trialDaysLeft: "λήγει σήμερα | απομένει {n} ημέρα | απομένουν {n} ημέρες",
				yearly:
					"Ετήσιο πρόγραμμα: {price} τον χρόνο, ανανεώνεται στις {date}. Οι πιστώσεις ανανεώνονται κάθε μήνα.",
				anchor: "κάθε μήνα στις {day}",
				window: "Κύκλος από {from} έως {to}, {anchor} — {rail}.",
				trialWindow: "Δωρεάν δοκιμή έως {date}: καμία χρέωση μέχρι τότε.",
				noSubscription: "Αυτό το πλάνο δεν έχει ακόμη συνδρομή: δεν χρεώνεται τίποτα αυτόματα.",
				noPlan: "Αυτός ο χώρος εργασίας δεν έχει ακόμη πλάνο.",
				noPlanHint:
					"Μπορείς να χρησιμοποιείς ολόκληρη την εφαρμογή χωρίς αυτό — εκείνο που δεν γίνεται είναι να καλέσεις την AI. Διάλεξε ένα πλάνο παρακάτω και οι πιστώσεις μπαίνουν την ίδια στιγμή, ή",
				noPlanLink: "δες το πλέγμα χωρίς την εφαρμογή γύρω του",
			},
			subscription: {
				title: "Συνδρομή",
				scheduledTitle: "Η συνδρομή ακυρώθηκε για το τέλος του κύκλου.",
				scheduledBody:
					"Το πλάνο ισχύει μέχρι {date} και δεν χρεώνεται τίποτα μετά. Οι πιστώσεις του πλάνου σταματούν να ανανεώνονται· το υπόλοιπο δώρου δεν λήγει.",
				reactivate: "Επανενεργοποίηση συνδρομής",
				noGateway:
					"Η αυτόματη πληρωμή δεν είναι ενεργή σε αυτή την εγκατάσταση. Τα πληρωμένα πλάνα χρεώνονται μόνο στην κάρτα, οπότε τίποτα δεν χρεώνεται εδώ μέχρι να ενεργοποιηθεί — στα Τιμολόγια βλέπεις ακόμη όλα τα τιμολόγια που έχουν εκδοθεί.",
				activate: "Ενεργοποίηση μηνιαίας πληρωμής",
				cancel: "Ακύρωση συνδρομής",
				cancelTitle: "Να ακυρωθεί η συνδρομή;",
				cancelDescription: "Η ακύρωση ισχύει στο τέλος του κύκλου — τίποτα δεν σταματά σήμερα.",
				cancelBody1:
					"Το πλάνο ισχύει μέχρι {date}. Μετά σταματά η ανανέωση των πιστώσεων και η AI παύει να απαντά· η εφαρμογή, οι συνομιλίες και οι εγγραφές παραμένουν προσβάσιμες.",
				cancelBody2:
					"Μπορείς να την επανενεργοποιήσεις οποιαδήποτε στιγμή πριν από εκείνη την ημερομηνία, από αυτή την ίδια οθόνη.",
				keep: "Διατήρηση συνδρομής",
				confirmCancel: "Ακύρωση στο τέλος του κύκλου",
				cancelFailed: "Δεν ήταν δυνατός ο προγραμματισμός της ακύρωσης. Δοκίμασε ξανά σε λίγο.",
				reactivateFailed: "Δεν ήταν δυνατή η επανενεργοποίηση της συνδρομής.",
				checkoutFailed: "Δεν ήταν δυνατό το άνοιγμα της πληρωμής τώρα.",
			},
		},

		credits: {
			chip: {
				credits: "{n} πιστώσεις",
				detail: "{plan} από το πλάνο και {bonus} δώρου.",
				renews: "Ανανεώνεται στις {date}.",
				paused: "AI σε παύση",
				pausedDetail:
					"Το πλάνο και το δώρο είναι στο μηδέν, οπότε η AI σταμάτησε να απαντά. Ένα πακέτο πιστώσεων επαναφέρει τις απαντήσεις αμέσως.",
				blocked: "AI σε φραγή",
				blockedDetail:
					"Φραγή από όποιον διαχειρίζεται την εγκατάσταση. Η αγορά πιστώσεων δεν την αίρει — μίλα με την υποστήριξη.",
			},
			meters: {
				title: "Πιστώσεις",
				blockedTitle: "Η AI είναι σε φραγή σε αυτόν τον χώρο εργασίας.",
				blockedByAdmin:
					"Η φραγή μπήκε από όποιον διαχειρίζεται την εγκατάσταση{reason}. Η αγορά πιστώσεων ΔΕΝ την αίρει — μίλα με την υποστήριξη.",
				reasonQuote: ": “{reason}”",
				blockedNoCredits:
					"Το υπόλοιπο έφτασε στο μηδέν. Ένα πακέτο πιστώσεων επαναφέρει αμέσως τις απαντήσεις, και η ανανέωση του πλάνου μπαίνει μόνη της στις {date}.",
				planLabel: "πιστώσεις πλάνου",
				percentUsed: "{percent}% του κύκλου καταναλώθηκε",
				renews: "ανανεώνεται στις {date}",
				noRenewal: "δεν έχει καταγραφεί ημερομηνία ανανέωσης",
				bonus: "δώρο",
				bonusHint: "δεν λήγει",
				total: "συνολικά διαθέσιμες",
				totalHint: "το πλάνο ξοδεύεται πρώτο",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "δοκιμαστικές πιστώσεις",
				trialUntil:
					"Ισχύουν έως {date}. Όταν πάρεις ένα πλάνο, ό,τι περισσέψει από τις πιστώσεις καλωσορίσματος της δοκιμής μένει δικό σου και προστίθεται στις πιστώσεις του πλάνου.",
			},
			packs: {
				title: "Αγορά πιστώσεων",
				subtitle: "χρεώνονται τώρα στην κάρτα· οι μονάδες μπαίνουν αμέσως μετά",
				noPlan: "Τα μεμονωμένα πακέτα είναι για όποιον έχει ήδη πλάνο.",
				noPlanHint:
					"Ένα πακέτο χρεώνεται στο τιμολόγιο του μήνα, και το τιμολόγιο γεννιέται από τον κύκλο του πλάνου. Διάλεξε πρώτα ένα πλάνο — οι μηνιαίες πιστώσεις του μπορεί να λύνουν ήδη το θέμα.",
				seePlans: "Προβολή πλάνων",
				empty: "Αυτή η εγκατάσταση δεν έχει δημοσιεύσει μεμονωμένα πακέτα.",
				credits: "{credits} πιστώσεις",
				perThousand: "{price} ανά 1.000 πιστώσεις",
				buy: "Αγορά",
				bought: "Αγοράστηκε το {name}. Οι πιστώσεις είναι ήδη στο υπόλοιπο δώρου.",
				failed: "Δεν ήταν δυνατή η αγορά του πακέτου. Δεν χρεώθηκε τίποτα.",
				confirmTitle: "Επιβεβαίωση της αγοράς",
				confirmDescription:
					"Η αποθηκευμένη σας κάρτα χρεώνεται τώρα. Οι μονάδες μπαίνουν μόλις περάσει η χρέωση.",
				confirmBody:
					"— {credits} πιστώσεις για {price}, με χρέωση τώρα στην αποθηκευμένη κάρτα σας. Πάνε στο υπόλοιπο δώρου, που δεν λήγει. Αν η κάρτα απορριφθεί, δεν χρεώνεται τίποτα και δεν προστίθενται μονάδες.",
				trialLocked: "Επιλέξτε πλάνο για να αγοράσετε μεμονωμένες πιστώσεις.",
				cardLocked:
					"Τα πακέτα χρεώνονται αμέσως στην κάρτα ενός πληρωμένου πλάνου, και αυτός ο χώρος εργασίας δεν έχει κάρτα για χρέωση.",
				notNow: "Όχι τώρα",
			},
			ledger: {
				title: "Κινήσεις πιστώσεων",
				subtitle: "ανανέωση, αλλαγή πλάνου, δώρο, πακέτο, προσαρμογή, αντιλογισμός",
				allKinds: "όλα τα είδη",
				includeConsumption: "Συμπερίληψη της κατανάλωσης στη λίστα",
				when: "πότε",
				kind: "είδος",
				pool: "προέλευση",
				credits: "πιστώσεις",
				planAfter: "υπόλοιπο πλάνου μετά",
				bonusAfter: "υπόλοιπο δώρου μετά",
				reason: "αιτιολογία",
				welcome: "Πιστώσεις καλωσορίσματος",
				welcomeTrial: "Πιστώσεις καλωσορίσματος της δοκιμής",
				poolPlan: "πλάνο",
				poolBonus: "δώρο",
				empty: "Καμία κίνηση πίστωσης σε αυτή την περίοδο.",
			},
		},

		limits: {
			meter: "{resource} σε χρήση",
			fromPlan: "{n} από το πλάνο",
			fromExtra: "+ {n} ως πρόσθετο",
			waived: "+ {n} σε παραχώρηση (δεν τιμολογείται)",
			percentUsed: "{percent}% σε χρήση",
			window: "Παράθυρο κύκλου: {start} έως {end}.",
			notEnforced:
				"Αυτός ο αριθμός παρακολουθείται και δεν μπλοκάρει ακόμη τίποτα σε αυτή την έκδοση: η υπέρβαση του ορίου δεν διακόπτει την εξυπηρέτηση.",
			enforcedContacts:
				"Οι νέες επαφές πάνω από το όριο γράφουν και τα μηνύματα φυλάσσονται, αλλά κανείς δεν τις απαντά μέχρι να αγοραστούν περισσότερες επαφές ή να αλλάξει ο κύκλος.",
			enforcedStorage:
				"Με γεμάτο αποθηκευτικό χώρο, τα ανεβάσματα της ομάδας απορρίπτονται. Τα μηνύματα που στέλνουν οι πελάτες σου συνεχίζουν να φτάνουν και φυλάσσονται.",
			scheduled: "Μείωση σε {quantity} προγραμματισμένη για {date}.",
			addonField: "Πρόσθετο για {resource}",
			unitPrice: "{price} ανά μονάδα/μήνα · {total} με ό,τι υπάρχει στο πεδίο",
			blockPrice: "{price} ανά {block}/μήνα · {total} με ό,τι υπάρχει στο πεδίο",
			blockField: "Πακέτα των {block}",
			blockCeiling: "{n} πακέτο = +{units} στο όριο | {n} πακέτα = +{units} στο όριο",
			notWholeBlocks:
				"Αυτή η προσθήκη πωλείται σε πακέτα των {block}. Ζητήστε ακέραιο αριθμό τους.",
			raise:
				"Η αύξηση από {from} σε {to} χρεώνει τώρα, αναλογικά για ό,τι απομένει από τον κύκλο, και ισχύει την ίδια στιγμή.",
			lowerAt:
				"Η μείωση από {from} σε {to} ισχύει στις {date}. Μέχρι τότε το όριο μένει όπως είναι σήμερα και το ποσό του μήνα δεν αλλάζει.",
			lowerEndOfCycle:
				"Η μείωση από {from} σε {to} ισχύει στο τέλος του κύκλου. Μέχρι τότε το όριο μένει όπως είναι σήμερα και το ποσό του μήνα δεν αλλάζει.",
			notSold:
				"Αυτό το πρόσθετο δεν πωλείται εδώ. Μίλα με όποιον διαχειρίζεται την εγκατάσταση για να ανέβει το όριο.",
			unlimited: "Απεριόριστο σε αυτό το πλάνο",
			unlimitedUsed: "{n} σε χρήση · χωρίς όριο από το πλάνο",
			offer: {
				title: "Το όριο του πλάνου συμπληρώθηκε",
				usage: "{used} από {limit} του πλάνου σε χρήση.",
				extrasLead: "Τα πρόσθετα πωλούνται σε μπλοκ, στην τιμή του πλάνου σου.",
				extrasAction: "Αύξηση ορίου",
				planLead: "Τα πρόσθετα έρχονται με πληρωμένο πλάνο.",
				planAction: "Διάλεξε πλάνο",
				noneAction: "Δες τα πλάνα",
			},
			monthly: "πρόσθετα ανά μήνα",
			discard: "Απόρριψη",
			save: "Αποθήκευση πρόσθετων",
			saved:
				"Τα πρόσθετα αποθηκεύτηκαν. Η αύξηση ισχύει ήδη· η μείωση μπαίνει στην αλλαγή του κύκλου.",
			failed:
				"Δεν ήταν δυνατή η αποθήκευση των πρόσθετων. Δεν χρεώθηκε τίποτα και δεν άλλαξε τίποτα.",
		},

		invoice: {
			period: "{from} έως {to}",
			paidOn: "Πληρώθηκε στις {date}",
			dueOn: "Λήγει στις {date}",
			noDue: "Δεν έχει καταγραφεί ημερομηνία λήξης",
			cardPack: "Πακέτο πιστώσεων · {credits} πιστώσεις · πληρώθηκε με κάρτα",
			receipt: "Απόδειξη",
			receiptMissing: "Η πύλη πληρωμών δεν επέστρεψε απόδειξη για αυτή τη χρέωση.",
			lines: {
				description: "περιγραφή",
				quantity: "ποσ.",
				unit: "μονάδα",
				amount: "ποσό",
				subtotal: "μερικό σύνολο",
			},
			pending: {
				overdueTitle: "Ληξιπρόθεσμο τιμολόγιο",
				openTitle: "Τιμολόγιο σε εκκρεμότητα",
				head: "{title} — {amount}, {month}",
				dueOn: "Λήγει στις {date}.",
				noDue: "Δεν έχει καταγραφεί ημερομηνία λήξης.",
				gatewayOn: "Η χρέωση επιχειρείται αυτόματα στην αποθηκευμένη κάρτα.",
				gatewayOff:
					"Η αυτόματη πληρωμή δεν είναι ενεργή εδώ: αυτό το τιμολόγιο πληρώνεται εκτός και σημειώνεται ως πληρωμένο από όποιον διαχειρίζεται την εγκατάσταση.",
				see: "Προβολή τιμολογίου",
			},
			preview: {
				title: "Προεπισκόπηση επόμενου τιμολογίου",
				empty: "Τίποτα προς τιμολόγηση σε αυτόν τον κύκλο μέχρι στιγμής.",
				total: "αναμενόμενο σύνολο",
			},
		},

		consumption: {
			from: "από",
			to: "έως",
			allSources: "όλες οι πηγές",
			rangeInverted:
				"Η τελική ημερομηνία είναι πριν από την αρχική. Η λίστα μένει στην τελευταία έγκυρη περίοδο.",
			sourceName: {
				panel: "πίνακας",
				channel: "κανάλι",
				mission: "αποστολή",
				automation: "αυτοματισμός",
			},
			calls: "απαντήσεις",
			credits: "πιστώσεις",
			avgDuration: "μέση διάρκεια",
			errors: "σφάλματα",
			when: "πότε",
			source: "προέλευση",
			agent: "πράκτορας",
			duration: "διάρκεια",
			state: "κατάσταση",
			failed: "σφάλμα",
			ok: "ok",
			failedDetail: "Αυτή η απάντηση απέτυχε.",
			turn: "δείτε ολόκληρη την απάντηση",
			noDetail: "Δεν καταγράφηκε λεπτομέρεια για αυτή την απάντηση.",
			toolsTitle: "Εργαλεία που χρεώθηκαν σε αυτόν τον γύρο",
			toolsLine: "{calls} × {rate} μονάδες",
			toolsShare: "{n} πίστωση σε εργαλεία | {n} πιστώσεις σε εργαλεία",
			empty: "Καμία απάντηση AI σε αυτή την περίοδο με αυτά τα φίλτρα.",
		},

		admin: {
			courtesy: {
				title: "Δωρεάν παροχή",
				endsOn: "Λήγει στην ανανέωση της {date}",
				endless: "Χωρίς ημερομηνία λήξης",
				card: "Υπάρχει κάρτα: η πρώτη χρέωση πλήρους τιμής γίνεται στις {date}.",
				noCard:
					"Δεν υπάρχει ακόμη κάρτα: αν ο πελάτης δεν εγγραφεί, ο χώρος εργασίας λήγει στις {date}.",
				date: "Δωρεάν έως",
				dateHint: "Λήγει στην ανανέωση που πέφτει σε αυτή την ημερομηνία ή μετά.",
				save: "Αποθήκευση ημερομηνίας",
				clear: "Αφαίρεση ημερομηνίας λήξης",
				failed: "Δεν ήταν δυνατή η αποθήκευση της ημερομηνίας λήξης.",
			},
			usage: {
				title: "Πρόγραμμα και χρήση",
				subtitle:
					"Τι χρησιμοποιεί ο χώρος εργασίας σε σχέση με όσα επιτρέπει το πρόγραμμά του — οι ίδιοι αριθμοί με τη δική του οθόνη ορίων.",
				plan: "Πρόγραμμα {plan} · {credits} μονάδες τον μήνα",
				none: "Αυτός ο χώρος εργασίας δεν έχει πρόγραμμα.",
				unlimited: "απεριόριστο",
			},
			margin: {
				title: "Κόστος έναντι εσόδων, ανά χώρο εργασίας",
				subtitle:
					"Τι χρεώνεται κάθε χώρος εργασίας, δίπλα σε ό,τι κόστισε το AI του στο σπίτι τις τελευταίες 30 ημέρες.",
				workspace: "Χώρος εργασίας",
				plan: "Πλάνο",
				charged: "Χρεώθηκε",
				credits: "Πιστώσεις",
				aiCost: "Κόστος AI (30η)",
				noPlan: "χωρίς πλάνο",
				empty: "Κανένας χώρος εργασίας για σύγκριση ακόμη.",
				note: "Το κόστος AI δεν είναι το μόνο κόστος, ούτε η συνδρομή το μόνο έσοδο — τα πακέτα και τα έξτρα μένουν εκτός. Οι δύο στήλες είναι σε διαφορετικά νομίσματα και δεν προστίθενται ποτέ: εδώ δεν φυλάσσεται ισοτιμία. Μια κακή σχέση είναι λόγος για έρευνα, όχι ετυμηγορία.",
				error: "Δεν ήταν δυνατή η φόρτωση της σύγκρισης. Φορτώστε ξανά τη σελίδα.",
			},
			kpis: {
				workspaces: "χώροι εργασίας",
				onRail: "στη ράγα {rail}",
				invoiced: "τιμολογημένα {month}",
				received: "εισπραγμένα",
				open: "σε εκκρεμότητα",
				overdue: "ληξιπρόθεσμα",
				note: "Ένας υγιής χώρος εργασίας βρίσκεται σε ακριβώς μία ράγα — αυτό είναι πρωτεύον κλειδί στη βάση δεδομένων, όχι υπόσχεση αυτής της οθόνης.",
				rule: "Τιμολογημένα = τιμολόγια που εκδόθηκαν τον μήνα + πακέτα πιστώσεων που χρεώθηκαν στην κάρτα τον μήνα. Εισπραγμένα = ό,τι πληρώθηκε τον μήνα, από οποιοδήποτε τιμολόγιο. Σε εκκρεμότητα και ληξιπρόθεσμα = ό,τι είναι απλήρωτο αυτή τη στιγμή. Μια απορριφθείσα χρέωση ή μια που περιμένει ακόμη 3-D Secure δεν μετράει.",
			},
			table: {
				searchPlaceholder: "αναζήτηση χώρου εργασίας",
				allRails: "όλες οι ράγες",
				allPlans: "όλα τα πλάνα",
				workspace: "χώρος εργασίας",
				plan: "πλάνο",
				rail: "ράγα",
				nextCharge: "επόμενη χρέωση",
				pending: "σε εκκρεμότητα",
				extrasMonthly: "πρόσθετα/μήνα",
				packs: "πακέτα",
				monthlyTotal: "σύνολο/μήνα",
				billedMonth: "τιμολογημένα τον μήνα",
				cardPacks: "{count} με κάρτα",
				credits: "πιστώσεις",
				closeCycle: "Κλείσιμο κύκλου",
				empty: "Κανένας χώρος εργασίας δεν ταιριάζει με αυτά τα φίλτρα.",
				alreadyClosed:
					"Ο κύκλος του {name} είχε ήδη κλείσει αυτόν τον μήνα — δεν χρεώθηκε τίποτα ξανά.",
				closed: "Ο κύκλος του {name} έκλεισε.",
				closedWithMonth: "Ο κύκλος του {name} έκλεισε ({month}).",
				closeFailed: "Δεν ήταν δυνατό το κλείσιμο του κύκλου του {name}. Δεν χρεώθηκε τίποτα.",
				closeFailedWhy:
					"Δεν ήταν δυνατό το κλείσιμο του κύκλου του {name}. {why} Δεν χρεώθηκε τίποτα.",
			},
			tenant: {
				title: "Χρέωση",
				subtitle: "πλάνο, πιστώσεις, πρόσθετα και το επόμενο τιμολόγιο",
				none: "Αυτός ο χώρος εργασίας δεν έχει ρυθμισμένη χρέωση.",
				planCredits: "πιστώσεις πλάνου",
				bonus: "δώρο",
				cost: "πραγματικό κόστος του κύκλου",
				costHint: "τι χρέωσαν οι πάροχοι",
				margin: "περιθώριο",
				marginHint: "δεν εμφανίζεται ποτέ στον πελάτη",
				plan: "πλάνο",
				noPlan: "χωρίς πλάνο",
				switchNow: "Αλλαγή τώρα",
				switchNote:
					"Η αλλαγή ισχύει αμέσως: οι πιστώσεις του νέου πλάνου μπαίνουν και το επαναλαμβανόμενο ποσό αλλάζει.",
				invoices: {
					title: "Ιστορικό πληρωμών",
					subtitle: "κάθε τιμολόγιο που έλαβε αυτός ο χώρος εργασίας, και πώς κατέληξε το καθένα",
					month: "μήνας",
					total: "ποσό",
					status: "κατάσταση",
					paidAt: "πληρώθηκε στις",
					method: "τρόπος",
					externalRef: "εξωτερική αναφορά",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "κάρτα (Stripe)" },
					none: "Δεν έχει εκδοθεί ακόμη τιμολόγιο για αυτόν τον χώρο εργασίας.",
				},
			},
			creditActions: {
				title: "Πιστώσεις, στο χέρι",
				delta: "πιστώσεις (±)",
				deltaPlaceholder: "500",
				pool: "τσέπη",
				poolPlan: "η τσέπη του πλάνου (χάνεται στην αλλαγή)",
				poolBonus: "δώρο (δεν λήγει)",
				reason: "αιτιολογία",
				reasonPlaceholder: "μένει στο καθολικό",
				post: "Καταχώριση προσαρμογής",
				needAmount: "Πες πόσες πιστώσεις, με πρόσημο — το αρνητικό χρεώνει.",
				needReason: "Η αιτιολογία μένει στο καθολικό. Είναι υποχρεωτική.",
				posted: "Η προσαρμογή καταχωρίστηκε στο καθολικό.",
				adjustFailed: "Δεν ήταν δυνατή η καταχώριση της προσαρμογής. Το υπόλοιπο δεν άλλαξε.",
				blockReason: "αιτιολογία της φραγής",
				needBlockReason:
					"Μια φραγή χωρίς αιτιολογία είναι μια φραγή που κανείς δεν μπορεί να εξηγήσει αργότερα.",
				blocked: "Φραγή AI με απόφαση της πλατφόρμας{reason}. Η ανανέωση πιστώσεων δεν την αίρει.",
				block: "Φραγή AI",
				unblock: "Άρση φραγής AI",
				doneBlocked: "Η AI μπήκε σε φραγή για αυτόν τον χώρο εργασίας.",
				doneUnblocked: "Η φραγή αφαιρέθηκε.",
				blockFailed: "Δεν ήταν δυνατή η αλλαγή της φραγής.",
			},
			extras: {
				resource: "πόρος",
				invoiced: "τιμολογημένο",
				waived: "παραχώρηση",
				unit: "μονάδα",
				subtotal: "μερικό σύνολο",
				invoicedField: "Τιμολογημένο πρόσθετο για {resource}",
				waivedField: "Πρόσθετο σε παραχώρηση για {resource}",
				scheduled: "{resource}: μείωση σε {quantity} προγραμματισμένη για {date}.",
				cancelSchedule: "Ακύρωση του προγραμματισμού",
				save: "Αποθήκευση (χρεώνει αναλογικά)",
				discard: "Απόρριψη",
				notForSale: "εκτός πώλησης",
				perBlock: "ανά {size}",
				total: "σύνολο πρόσθετων",
				failed: "Δεν ήταν δυνατή η αποθήκευση των πρόσθετων. Δεν χρεώθηκε τίποτα.",
			},
		},

		pagination: {
			nav: "Σελιδοποίηση",
			of: "από",
			perPage: "Ανά σελίδα",
			previous: "Προηγούμενη",
			next: "Επόμενη",
			page: "Σελίδα",
			goToPage: "Μετάβαση στη σελίδα",
			goTo: "Μετάβαση σε",
		},
	},

	es: {
		waiver: {
			until: "Plan de cortesía hasta el {date}",
			untilSetupHint:
				"Configura el pago antes del {date} para mantener tu plan. No se cobra nada hasta entonces: el primer cobro es el {date}.",
			untilConfigured: "Pago configurado. El primer cobro es el {date}.",
			label: "Eximir la cuota de este plan",
			hint: "Mantiene el plan seleccionado y sus límites. Las compras nuevas se cobran por separado.",
			active: "Cuota del plan bonificada",
			setup: "Configurar pago",
			setupHint:
				"Tu plan sigue bonificado. Añade un método de pago en Stripe para comprar extras o créditos.",
			failed: "No se pudo abrir la configuración de pago.",
			free: "Plan gratuito",
			freeSetupHint:
				"Úsalo sin tarjeta. Agrega una cuando quieras comprar créditos o complementos — no se cobra nada ahora.",
		},
		nav: {
			aria: "Secciones de facturación",
			plan: "Mi plan",
			credits: "Créditos",
			limits: "Límites",
			invoices: "Facturas",
			consumption: "Consumo",
		},

		resource: {
			users: {
				label: "usuarios",
				hint: "personas con acceso a este espacio de trabajo",
				amount: "{n} usuario | {n} usuarios",
				quantity: "{n}",
			},
			channels: {
				label: "canales",
				hint: "números, bandejas y chats conectados",
				amount: "{n} canal | {n} canales",
				quantity: "{n}",
			},
			storage_gb: {
				label: "almacenamiento",
				hint: "archivos, medios y adjuntos guardados",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "contactos activos",
				hint: "personas distintas atendidas en el mes",
				amount: "{n} contacto activo | {n} contactos activos",
				quantity: "{n}",
			},
		},

		rail: {
			none: "sin cobro",
			gateway: { label: "tarjeta", hint: "se cobra automáticamente en la tarjeta" },
			free: { label: "gratuito", hint: "no se cobra" },
			courtesy: { label: "cortesía", hint: "no se cobra" },
			trial: { label: "prueba", hint: "en el período de prueba gratis — aún sin cobro" },
		},

		subscriptionStatus: {
			active: "activa",
			trialing: "en prueba",
			past_due: "pago pendiente",
			canceled: "cancelada",
			incomplete: "incompleta",
			expired: "vencida",
		},
		invoiceStatus: { open: "pendiente", paid: "pagada", overdue: "vencida", void: "anulada" },
		ledgerKind: {
			consumption: "consumo",
			debit: "consumo",
			refill: "renovación del ciclo",
			plan_change: "cambio de plan",
			trial_end: "fin de la prueba",
			plan_end: "fin del plan",
			bonus: "bonificación",
			pack: "paquete comprado",
			adjustment: "ajuste",
			reversal: "reversión",
			expiry: "caducidad",
		},

		plan: {
			card: {
				current: "Plan actual",
				select: "Seleccionar",
				upgrade: "Subir de plan",
				downgrade: "Bajar de plan",
				switch: "Cambiar",
				featured: "destacado",
				perMonth: "/mes",
				creditsPerMonth: "{credits} créditos al mes",
				smartAnswers: "≈ {n} respuestas",
				smartAnswersRecent: "≈ {n} respuestas, según tu uso reciente",
				noLimit: "sin límite",
				users: "usuarios",
				channels: "canales",
				storage: "almacenamiento",
				contacts: "contactos/mes",
				units: "workspaces",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "No se pudo leer el catálogo de planes",
				errorHint: "Es un fallo nuestro, no una falta de planes. Inténtalo de nuevo en un momento.",
				emptyTitle: "Ningún plan publicado",
				emptyHint: "Esta instalación aún no ha publicado un catálogo comercial.",
				failed: "No se pudo cambiar el plan ahora. No se ha cobrado nada.",
				noGateway:
					"Los planes de pago se cobran con tarjeta, y el pago con tarjeta no está activado en esta instalación — no se puede elegir un plan de pago aquí hasta que lo esté.",
			},
			cycle: {
				title: "Mi plan",
				creditsPerMonth: "{credits} créditos al mes",
				trialCredits: "{credits} créditos para probar",
				monthly: "plan",
				extras: "adicionales",
				total: "total al mes",
				nextCharge: "próximo cobro",
				noNextCharge: "ninguno previsto",
				trialEnds: "fin de la prueba",
				trialDaysLeft: "termina hoy | queda {n} día | quedan {n} días",
				yearly:
					"Plan anual: {price} al año, se renueva el {date}. Los créditos se recargan cada mes.",
				anchor: "cada mes el día {day}",
				window: "Ciclo del {from} al {to}, {anchor} — {rail}.",
				trialWindow: "Prueba gratis hasta el {date}: no se cobra nada hasta entonces.",
				noSubscription: "Este plan aún no tiene suscripción: no se cobra nada automáticamente.",
				noPlan: "Este espacio de trabajo aún no tiene plan.",
				noPlanHint:
					"Puedes usar todo el panel sin uno — lo que no puedes es llamar a la IA. Elige un plan abajo y los créditos entran en ese mismo instante, o",
				noPlanLink: "mira la cuadrícula sin el panel alrededor",
			},
			subscription: {
				title: "Suscripción",
				scheduledTitle: "Suscripción cancelada para el final del ciclo.",
				scheduledBody:
					"El plan sigue vigente hasta {date} y no se cobra nada después. Los créditos del plan dejan de renovarse; el saldo de bonificación no caduca.",
				reactivate: "Reactivar suscripción",
				noGateway:
					"El pago automático no está activado en esta instalación. Los planes de pago se cobran solo con tarjeta, así que aquí no se cobra nada hasta que lo esté — en Facturas sigues viendo todas las facturas ya emitidas.",
				activate: "Activar el pago mensual",
				cancel: "Cancelar suscripción",
				cancelTitle: "¿Cancelar la suscripción?",
				cancelDescription:
					"La cancelación surte efecto al final del ciclo — hoy no se interrumpe nada.",
				cancelBody1:
					"El plan sigue hasta {date}. Después la renovación de créditos se detiene y la IA deja de responder; el panel, las conversaciones y los registros siguen accesibles.",
				cancelBody2:
					"Puedes reactivarla en cualquier momento antes de esa fecha, desde esta misma pantalla.",
				keep: "Mantener suscripción",
				confirmCancel: "Cancelar al final del ciclo",
				cancelFailed: "No se pudo programar la cancelación. Inténtalo de nuevo en un momento.",
				reactivateFailed: "No se pudo reactivar la suscripción.",
				checkoutFailed: "No se pudo abrir el pago ahora.",
			},
		},

		credits: {
			chip: {
				credits: "{n} créditos",
				detail: "{plan} del plan y {bonus} de bonificación.",
				renews: "Se renueva el {date}.",
				paused: "IA en pausa",
				pausedDetail:
					"El plan y la bonificación están a cero, así que la IA dejó de responder. Un paquete de créditos la desbloquea al instante.",
				blocked: "IA bloqueada",
				blockedDetail:
					"Bloqueo de quien administra la instalación. Comprar créditos no lo deshace — habla con soporte.",
			},
			meters: {
				title: "Créditos",
				blockedTitle: "IA bloqueada en este espacio de trabajo.",
				blockedByAdmin:
					"El bloqueo lo puso quien administra la instalación{reason}. Comprar créditos NO lo deshace — habla con soporte.",
				reasonQuote: ": “{reason}”",
				blockedNoCredits:
					"El saldo llegó a cero. Un paquete de créditos desbloquea las respuestas al instante, y la renovación del plan entra sola el {date}.",
				planLabel: "créditos del plan",
				percentUsed: "{percent}% del ciclo consumido",
				renews: "se renueva el {date}",
				noRenewal: "sin fecha de renovación registrada",
				bonus: "bonificación",
				bonusHint: "no caduca",
				total: "total disponible",
				totalHint: "el plan se gasta primero",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "créditos de prueba",
				trialUntil:
					"Válidos hasta el {date}. Al contratar un plan, lo que sobre de los créditos de bienvenida de la prueba sigue contigo y se suma a los créditos del plan.",
			},
			packs: {
				title: "Comprar créditos",
				subtitle: "se cobran ahora en su tarjeta; los créditos entran justo después",
				noPlan: "Los paquetes sueltos son para quien ya tiene un plan.",
				noPlanHint:
					"Un paquete se cobra en la factura del mes, y la factura nace del ciclo del plan. Elige antes un plan — sus créditos mensuales quizá ya lo resuelvan.",
				seePlans: "Ver los planes",
				empty: "Esta instalación no ha publicado paquetes sueltos.",
				credits: "{credits} créditos",
				perThousand: "{price} por cada 1.000 créditos",
				buy: "Comprar",
				bought: "{name} comprado. Los créditos ya están en el saldo de bonificación.",
				failed: "No se pudo comprar el paquete. No se ha cobrado nada.",
				confirmTitle: "Confirmar la compra",
				confirmDescription:
					"Su tarjeta guardada se cobra ahora. Los créditos entran en cuanto pase el cobro.",
				confirmBody:
					"— {credits} créditos por {price}, cobrados ahora en su tarjeta guardada. Van al saldo de bonificación, que no caduca. Si la tarjeta es rechazada, no se cobra nada y no entran créditos.",
				trialLocked: "Elija un plan para comprar créditos sueltos.",
				cardLocked:
					"Los paquetes se cobran al momento en la tarjeta de un plan de pago, y este espacio de trabajo no tiene tarjeta donde cobrar.",
				notNow: "Ahora no",
			},
			ledger: {
				title: "Movimientos de créditos",
				subtitle: "renovación, cambio de plan, bonificación, paquete, ajuste, reversión",
				allKinds: "todos los tipos",
				includeConsumption: "Incluir el consumo en la lista",
				when: "cuándo",
				kind: "tipo",
				pool: "origen",
				credits: "créditos",
				planAfter: "saldo del plan después",
				bonusAfter: "saldo de bonificación después",
				reason: "motivo",
				welcome: "Créditos de bienvenida",
				welcomeTrial: "Créditos de bienvenida de la prueba",
				poolPlan: "plan",
				poolBonus: "bonificación",
				empty: "Ningún movimiento de crédito en este periodo.",
			},
		},

		limits: {
			meter: "{resource} en uso",
			fromPlan: "{n} del plan",
			fromExtra: "+ {n} como adicional",
			waived: "+ {n} exentos (no facturados)",
			percentUsed: "{percent}% usado",
			window: "Ventana del ciclo: {start} a {end}.",
			notEnforced:
				"Este número se registra y todavía no bloquea nada en esta versión: pasarse del límite no interrumpe el servicio.",
			enforcedContacts:
				"Los contactos nuevos por encima del límite escriben y sus mensajes se guardan, pero nadie los atiende hasta comprar más contactos o que cambie el ciclo.",
			enforcedStorage:
				"Con el almacenamiento lleno, las subidas del equipo se rechazan. Los mensajes que envían tus clientes siguen llegando y se guardan.",
			scheduled: "Reducción a {quantity} programada para el {date}.",
			addonField: "Adicional de {resource}",
			unitPrice: "{price} por unidad/mes · {total} con lo que hay en el campo",
			blockPrice: "{price} por cada {block}/mes · {total} con lo que hay en el campo",
			blockField: "Bloques de {block}",
			blockCeiling: "{n} bloque = +{units} en el tope | {n} bloques = +{units} en el tope",
			notWholeBlocks:
				"Este adicional se vende en bloques de {block}. Pida un número entero de ellos.",
			raise:
				"Subir de {from} a {to} se cobra ahora, a prorrata de lo que queda del ciclo, y surte efecto en ese mismo instante.",
			lowerAt:
				"Bajar de {from} a {to} surte efecto el {date}. Hasta entonces el límite sigue como hoy y el importe del mes no cambia.",
			lowerEndOfCycle:
				"Bajar de {from} a {to} surte efecto al final del ciclo. Hasta entonces el límite sigue como hoy y el importe del mes no cambia.",
			notSold:
				"Este adicional no se vende aquí. Habla con quien administra la instalación para subir el límite.",
			unlimited: "Ilimitado en este plan",
			unlimitedUsed: "{n} en uso · sin tope del plan",
			offer: {
				title: "Límite del plan alcanzado",
				usage: "{used} de {limit} del plan en uso.",
				extrasLead: "Los adicionales se venden en bloques, al precio de tu plan.",
				extrasAction: "Ampliar límite",
				planLead: "Los adicionales entran con un plan de pago.",
				planAction: "Elegir un plan",
				noneAction: "Ver planes",
			},
			monthly: "adicionales al mes",
			discard: "Descartar",
			save: "Guardar adicionales",
			saved: "Adicionales guardados. La subida ya se aplica; la bajada entra al cambio de ciclo.",
			failed: "No se pudieron guardar los adicionales. No se ha cobrado nada y nada ha cambiado.",
		},

		invoice: {
			period: "{from} a {to}",
			paidOn: "Pagada el {date}",
			dueOn: "Vence el {date}",
			noDue: "Sin vencimiento registrado",
			cardPack: "Paquete de créditos · {credits} créditos · pagado con tarjeta",
			receipt: "Recibo",
			receiptMissing: "La pasarela de pago no devolvió recibo para este cobro.",
			lines: {
				description: "descripción",
				quantity: "cant.",
				unit: "unidad",
				amount: "importe",
				subtotal: "subtotal",
			},
			pending: {
				overdueTitle: "Factura vencida",
				openTitle: "Factura pendiente",
				head: "{title} — {amount}, {month}",
				dueOn: "Vence el {date}.",
				noDue: "Sin fecha de vencimiento registrada.",
				gatewayOn: "El cobro se intenta automáticamente en la tarjeta guardada.",
				gatewayOff:
					"El pago automático no está activado aquí: esta factura se paga por fuera y la marca como pagada quien administra la instalación.",
				see: "Ver la factura",
			},
			preview: {
				title: "Vista previa de la próxima factura",
				empty: "Nada que facturar en este ciclo por ahora.",
				total: "total previsto",
			},
		},

		consumption: {
			from: "de",
			to: "hasta",
			allSources: "todos los orígenes",
			rangeInverted:
				"La fecha final es anterior a la inicial. La lista sigue en el último período válido.",
			sourceName: {
				panel: "panel",
				channel: "canal",
				mission: "misión",
				automation: "automatización",
			},
			calls: "respuestas",
			credits: "créditos",
			avgDuration: "duración media",
			errors: "errores",
			when: "cuándo",
			source: "origen",
			agent: "agente",
			duration: "duración",
			state: "estado",
			failed: "error",
			ok: "ok",
			failedDetail: "Esta respuesta falló.",
			turn: "ver esta respuesta completa",
			noDetail: "Sin detalle registrado para esta respuesta.",
			toolsTitle: "Herramientas cobradas en este turno",
			toolsLine: "{calls} × {rate} créditos",
			toolsShare: "{n} crédito en herramientas | {n} créditos en herramientas",
			empty: "Ninguna respuesta de IA en este periodo con estos filtros.",
		},

		admin: {
			courtesy: {
				title: "Cortesía",
				endsOn: "Termina en la renovación del {date}",
				endless: "Sin fecha de fin",
				card: "Tarjeta registrada: el primer cobro a precio completo es el {date}.",
				noCard: "Aún sin tarjeta: si el cliente no se suscribe, el espacio vence el {date}.",
				date: "Cortesía hasta",
				dateHint: "Termina en la renovación que caiga en esta fecha o después.",
				save: "Guardar fecha",
				clear: "Quitar fecha de fin",
				failed: "No se pudo guardar la fecha de fin de la cortesía.",
			},
			usage: {
				title: "Plan y uso",
				subtitle:
					"Lo que el espacio usa frente a lo que su plan permite: los mismos números de su pantalla de límites.",
				plan: "Plan {plan} · {credits} créditos al mes",
				none: "Este espacio no tiene plan.",
				unlimited: "ilimitado",
			},
			margin: {
				title: "Coste frente a ingreso, por espacio",
				subtitle:
					"Lo que se cobra a cada espacio, junto a lo que su IA le costó a esta casa en los últimos 30 días.",
				workspace: "Espacio",
				plan: "Plan",
				charged: "Cobrado",
				credits: "Créditos",
				aiCost: "Coste de IA (30d)",
				noPlan: "sin plan",
				empty: "Todavía no hay espacios que comparar.",
				note: "El coste de IA no es el único coste, y la cuota no es el único ingreso — los packs y los extras quedan fuera de esta tabla. Las dos columnas están además en monedas distintas y nunca se suman: aquí no se guarda ningún tipo de cambio. Una relación mala es motivo para investigar, no un veredicto.",
				error: "No se pudo cargar la comparación. Recarga la página.",
			},
			kpis: {
				workspaces: "espacios de trabajo",
				onRail: "en la vía {rail}",
				invoiced: "facturado en {month}",
				received: "cobrado",
				open: "pendiente",
				overdue: "vencido",
				note: "Un espacio de trabajo sano está en exactamente una vía — eso es clave primaria en la base de datos, no una promesa de esta pantalla.",
				rule: "Facturado = facturas emitidas en el mes + paquetes de créditos cobrados a la tarjeta en el mes. Cobrado = lo que se pagó en el mes, de cualquier factura. Pendiente y vencido = todo lo que está sin pagar ahora. Un cobro rechazado o que aún espera el 3-D Secure no cuenta.",
			},
			table: {
				searchPlaceholder: "buscar espacio de trabajo",
				allRails: "todas las vías",
				allPlans: "todos los planes",
				workspace: "espacio de trabajo",
				plan: "plan",
				rail: "vía",
				nextCharge: "próximo cobro",
				pending: "pendiente",
				extrasMonthly: "adicionales/mes",
				packs: "paquetes",
				monthlyTotal: "total/mes",
				billedMonth: "facturado en el mes",
				cardPacks: "{count} con tarjeta",
				credits: "créditos",
				closeCycle: "Cerrar ciclo",
				empty: "Ningún espacio de trabajo con estos filtros.",
				alreadyClosed:
					"El ciclo de {name} ya estaba cerrado este mes — no se ha cobrado nada de nuevo.",
				closed: "Ciclo de {name} cerrado.",
				closedWithMonth: "Ciclo de {name} cerrado ({month}).",
				closeFailed: "No se pudo cerrar el ciclo de {name}. No se ha cobrado nada.",
				closeFailedWhy: "No se pudo cerrar el ciclo de {name}. {why} No se ha cobrado nada.",
			},
			tenant: {
				title: "Facturación",
				subtitle: "plan, créditos, adicionales y la próxima factura",
				none: "Este espacio de trabajo no tiene facturación configurada.",
				planCredits: "créditos del plan",
				bonus: "bonificación",
				cost: "coste real del ciclo",
				costHint: "lo que cobraron los proveedores",
				margin: "margen",
				marginHint: "nunca se muestra al cliente",
				plan: "plan",
				noPlan: "sin plan",
				switchNow: "Cambiar ahora",
				switchNote:
					"El cambio surte efecto al instante: entran los créditos del plan nuevo y el importe recurrente cambia.",
				invoices: {
					title: "Historial de pagos",
					subtitle:
						"todas las facturas que ha recibido este espacio de trabajo, y cómo terminó cada una",
					month: "mes",
					total: "importe",
					status: "estado",
					paidAt: "pagada el",
					method: "forma",
					externalRef: "referencia externa",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "tarjeta (Stripe)" },
					none: "Todavía no se ha emitido ninguna factura para este espacio de trabajo.",
				},
			},
			creditActions: {
				title: "Créditos, a mano",
				delta: "créditos (±)",
				deltaPlaceholder: "500",
				pool: "bolsillo",
				poolPlan: "el bolsillo del plan (desaparece al cambio)",
				poolBonus: "bonificación (no caduca)",
				reason: "motivo",
				reasonPlaceholder: "queda en el libro mayor",
				post: "Registrar ajuste",
				needAmount: "Di cuántos créditos, con signo — el negativo descuenta.",
				needReason: "El motivo queda en el libro mayor. Es obligatorio.",
				posted: "Ajuste registrado en el libro mayor.",
				adjustFailed: "No se pudo registrar el ajuste. El saldo no ha cambiado.",
				blockReason: "motivo del bloqueo",
				needBlockReason: "Un bloqueo sin motivo es un bloqueo que luego nadie sabe explicar.",
				blocked:
					"IA bloqueada por decisión de la plataforma{reason}. Recargar créditos no lo deshace.",
				block: "Bloquear IA",
				unblock: "Desbloquear IA",
				doneBlocked: "IA bloqueada para este espacio de trabajo.",
				doneUnblocked: "Bloqueo retirado.",
				blockFailed: "No se pudo cambiar el bloqueo.",
			},
			extras: {
				resource: "recurso",
				invoiced: "facturado",
				waived: "exento",
				unit: "unidad",
				subtotal: "subtotal",
				invoicedField: "Adicional facturado de {resource}",
				waivedField: "Adicional exento de {resource}",
				scheduled: "{resource}: reducción a {quantity} programada para el {date}.",
				cancelSchedule: "Cancelar la programación",
				save: "Guardar (cobra a prorrata)",
				discard: "Descartar",
				notForSale: "fuera de venta",
				perBlock: "cada {size}",
				total: "total de adicionales",
				failed: "No se pudieron guardar los adicionales. No se ha cobrado nada.",
			},
		},

		pagination: {
			nav: "Paginación",
			of: "de",
			perPage: "Por página",
			previous: "Anterior",
			next: "Siguiente",
			page: "Página",
			goToPage: "Ir a la página",
			goTo: "Ir a",
		},
	},

	fr: {
		waiver: {
			until: "Forfait offert jusqu'au {date}",
			untilSetupHint:
				"Configurez le paiement avant le {date} pour garder votre forfait. Rien n'est prélevé d'ici là : le premier prélèvement a lieu le {date}.",
			untilConfigured: "Paiement configuré. Le premier prélèvement a lieu le {date}.",
			label: "Offrir l’abonnement à ce forfait",
			hint: "Conserve le forfait sélectionné et ses limites. Les nouveaux achats sont facturés séparément.",
			active: "Abonnement au forfait offert",
			setup: "Configurer le paiement",
			setupHint:
				"Votre forfait reste offert. Ajoutez un moyen de paiement dans Stripe pour acheter des suppléments ou des crédits.",
			failed: "Impossible d’ouvrir la configuration du paiement.",
			free: "Forfait gratuit",
			freeSetupHint:
				"Utilisez-le sans carte. Ajoutez-en une quand vous voulez acheter des crédits ou des options — rien n’est débité maintenant.",
		},
		nav: {
			aria: "Sections de facturation",
			plan: "Mon forfait",
			credits: "Crédits",
			limits: "Limites",
			invoices: "Factures",
			consumption: "Consommation",
		},

		resource: {
			users: {
				label: "utilisateurs",
				hint: "personnes ayant accès à cet espace de travail",
				amount: "{n} utilisateur | {n} utilisateurs",
				quantity: "{n}",
			},
			channels: {
				label: "canaux",
				hint: "numéros, boîtes de réception et chats connectés",
				amount: "{n} canal | {n} canaux",
				quantity: "{n}",
			},
			storage_gb: {
				label: "stockage",
				hint: "fichiers, médias et pièces jointes conservés",
				amount: "{n} Go",
				quantity: "{n} Go",
			},
			contacts: {
				label: "contacts actifs",
				hint: "personnes distinctes servies dans le mois",
				amount: "{n} contact actif | {n} contacts actifs",
				quantity: "{n}",
			},
		},

		rail: {
			none: "sans facturation",
			gateway: { label: "carte", hint: "prélevé automatiquement sur la carte" },
			free: { label: "gratuit", hint: "non facturé" },
			courtesy: { label: "offert", hint: "non facturé" },
			trial: { label: "essai", hint: "en période d’essai gratuite — pas encore facturé" },
		},

		subscriptionStatus: {
			active: "actif",
			trialing: "en essai",
			past_due: "impayé",
			canceled: "résilié",
			incomplete: "incomplet",
			expired: "expirée",
		},
		invoiceStatus: { open: "en attente", paid: "payée", overdue: "en retard", void: "annulée" },
		ledgerKind: {
			consumption: "consommation",
			debit: "consommation",
			refill: "renouvellement du cycle",
			plan_change: "changement de forfait",
			trial_end: "fin de l'essai",
			plan_end: "fin du forfait",
			bonus: "bonus",
			pack: "pack acheté",
			adjustment: "ajustement",
			reversal: "extourne",
			expiry: "expiration",
		},

		plan: {
			card: {
				current: "Forfait actuel",
				select: "Choisir",
				upgrade: "Monter",
				downgrade: "Descendre",
				switch: "Changer",
				featured: "recommandé",
				perMonth: "/mois",
				creditsPerMonth: "{credits} crédits par mois",
				smartAnswers: "≈ {n} réponses",
				smartAnswersRecent: "≈ {n} réponses, selon votre usage récent",
				noLimit: "sans limite",
				users: "utilisateurs",
				channels: "canaux",
				storage: "stockage",
				contacts: "contacts/mois",
				units: "espaces de travail",
				gb: "{n} Go",
			},
			grid: {
				errorTitle: "Impossible de lire le catalogue des forfaits",
				errorHint:
					"C'est une panne de notre côté, pas un manque de forfaits. Réessayez dans un instant.",
				emptyTitle: "Aucun forfait publié",
				emptyHint: "Cette installation n'a pas encore publié de catalogue commercial.",
				failed: "Impossible de changer de forfait pour l'instant. Rien n'a été facturé.",
				noGateway:
					"Les forfaits payants sont prélevés par carte, et le paiement par carte n'est pas activé sur cette installation — un forfait payant ne peut pas être choisi ici tant qu'il ne l'est pas.",
			},
			cycle: {
				title: "Mon forfait",
				creditsPerMonth: "{credits} crédits par mois",
				trialCredits: "{credits} crédits pour essayer",
				monthly: "forfait",
				extras: "options",
				total: "total par mois",
				nextCharge: "prochain prélèvement",
				noNextCharge: "aucun prévu",
				trialEnds: "fin de l'essai",
				trialDaysLeft: "se termine aujourd’hui | {n} jour restant | {n} jours restants",
				yearly:
					"Formule annuelle : {price} par an, renouvelée le {date}. Les crédits se rechargent chaque mois.",
				anchor: "chaque mois le {day}",
				window: "Cycle du {from} au {to}, {anchor} — {rail}.",
				trialWindow: "Essai gratuit jusqu'au {date} : rien n'est facturé d'ici là.",
				noSubscription:
					"Ce forfait n'a pas encore d'abonnement : rien n'est prélevé automatiquement.",
				noPlan: "Cet espace de travail n'a pas encore de forfait.",
				noPlanHint:
					"Vous pouvez utiliser tout le panneau sans forfait — ce que vous ne pouvez pas, c'est appeler l'IA. Choisissez un forfait ci-dessous et les crédits arrivent au même instant, ou",
				noPlanLink: "voyez la grille sans le panneau autour",
			},
			subscription: {
				title: "Abonnement",
				scheduledTitle: "Abonnement résilié pour la fin du cycle.",
				scheduledBody:
					"Le forfait court jusqu'au {date} et rien n'est prélevé après. Les crédits du forfait cessent de se renouveler ; le solde bonus n'expire pas.",
				reactivate: "Réactiver l'abonnement",
				noGateway:
					"Le paiement automatique n'est pas activé sur cette installation. Les forfaits payants sont prélevés uniquement par carte, donc rien n'est facturé ici tant qu'il ne l'est pas — dans Factures vous voyez toujours les factures déjà émises.",
				activate: "Activer le paiement mensuel",
				cancel: "Résilier l'abonnement",
				cancelTitle: "Résilier l'abonnement ?",
				cancelDescription:
					"La résiliation prend effet à la fin du cycle — rien ne s'arrête aujourd'hui.",
				cancelBody1:
					"Le forfait court jusqu'au {date}. Après cela le renouvellement des crédits s'arrête et l'IA cesse de répondre ; le panneau, les conversations et les fiches restent accessibles.",
				cancelBody2: "Vous pouvez réactiver à tout moment avant cette date, depuis ce même écran.",
				keep: "Garder l'abonnement",
				confirmCancel: "Résilier à la fin du cycle",
				cancelFailed: "Impossible de programmer la résiliation. Réessayez dans un instant.",
				reactivateFailed: "Impossible de réactiver l'abonnement.",
				checkoutFailed: "Impossible d'ouvrir le paiement pour l'instant.",
			},
		},

		credits: {
			chip: {
				credits: "{n} crédits",
				detail: "{plan} du forfait et {bonus} en bonus.",
				renews: "Renouvellement le {date}.",
				paused: "IA en pause",
				pausedDetail:
					"Le forfait et le bonus sont à zéro, l'IA a donc cessé de répondre. Un pack de crédits la débloque immédiatement.",
				blocked: "IA bloquée",
				blockedDetail:
					"Blocage posé par qui administre l'installation. Acheter des crédits ne le lève pas — contactez le support.",
			},
			meters: {
				title: "Crédits",
				blockedTitle: "IA bloquée dans cet espace de travail.",
				blockedByAdmin:
					"Le blocage a été posé par qui administre l'installation{reason}. Acheter des crédits ne le lève PAS — contactez le support.",
				reasonQuote: ": « {reason} »",
				blockedNoCredits:
					"Le solde est tombé à zéro. Un pack de crédits rétablit les réponses immédiatement, et le renouvellement du forfait arrive tout seul le {date}.",
				planLabel: "crédits du forfait",
				percentUsed: "{percent}% du cycle consommé",
				renews: "renouvellement le {date}",
				noRenewal: "aucune date de renouvellement enregistrée",
				bonus: "bonus",
				bonusHint: "n'expire pas",
				total: "total disponible",
				totalHint: "le forfait est consommé en premier",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "crédits d'essai",
				trialUntil:
					"Valables jusqu'au {date}. En souscrivant un forfait, ce qui reste des crédits de bienvenue de l'essai vous est conservé et s'ajoute aux crédits du forfait.",
			},
			packs: {
				title: "Acheter des crédits",
				subtitle: "débités maintenant sur votre carte ; les crédits arrivent juste après",
				noPlan: "Les packs à l'unité sont pour qui a déjà un forfait.",
				noPlanHint:
					"Un pack est porté sur la facture du mois, et la facture naît du cycle du forfait. Choisissez d'abord un forfait — ses crédits mensuels suffisent peut-être déjà.",
				seePlans: "Voir les forfaits",
				empty: "Cette installation n'a pas publié de packs à l'unité.",
				credits: "{credits} crédits",
				perThousand: "{price} pour 1 000 crédits",
				buy: "Acheter",
				bought: "{name} acheté. Les crédits sont déjà sur le solde bonus.",
				failed: "Impossible d'acheter le pack. Rien n'a été facturé.",
				confirmTitle: "Confirmer l'achat",
				confirmDescription:
					"Votre carte enregistrée est débitée maintenant. Les crédits arrivent dès que le débit passe.",
				confirmBody:
					"— {credits} crédits pour {price}, débités maintenant sur votre carte enregistrée. Ils vont sur le solde bonus, qui n'expire pas. Si la carte est refusée, rien n'est débité et aucun crédit n'entre.",
				trialLocked: "Choisissez un forfait pour acheter des crédits à l'unité.",
				cardLocked:
					"Les packs sont prélevés immédiatement sur la carte d'un forfait payant, et cet espace de travail n'a pas de carte à débiter.",
				notNow: "Pas maintenant",
			},
			ledger: {
				title: "Écritures de crédits",
				subtitle: "renouvellement, changement de forfait, bonus, pack, ajustement, extourne",
				allKinds: "tous les types",
				includeConsumption: "Inclure la consommation dans la liste",
				when: "quand",
				kind: "type",
				pool: "origine",
				credits: "crédits",
				planAfter: "solde du forfait après",
				bonusAfter: "solde bonus après",
				reason: "motif",
				welcome: "Crédits de bienvenue",
				welcomeTrial: "Crédits de bienvenue de l’essai",
				poolPlan: "forfait",
				poolBonus: "bonus",
				empty: "Aucune écriture de crédit sur cette période.",
			},
		},

		limits: {
			meter: "{resource} en usage",
			fromPlan: "{n} du forfait",
			fromExtra: "+ {n} en option",
			waived: "+ {n} offerts (non facturés)",
			percentUsed: "{percent}% utilisé",
			window: "Fenêtre du cycle : {start} au {end}.",
			notEnforced:
				"Ce nombre est suivi et ne bloque encore rien dans cette version : dépasser la limite n'interrompt pas le service.",
			enforcedContacts:
				"Les nouveaux contacts au-delà de la limite écrivent et leurs messages sont conservés, mais personne n'y répond tant que d'autres contacts ne sont pas achetés ou que le cycle ne change pas.",
			enforcedStorage:
				"Quand le stockage est plein, les envois de l'équipe sont refusés. Les messages de vos clients continuent d'arriver et sont conservés.",
			scheduled: "Réduction à {quantity} programmée pour le {date}.",
			addonField: "Option pour {resource}",
			unitPrice: "{price} par unité/mois · {total} avec ce qui est dans le champ",
			blockPrice: "{price} par tranche de {block}/mois · {total} avec ce qui est dans le champ",
			blockField: "Tranches de {block}",
			blockCeiling:
				"{n} tranche = +{units} sur le plafond | {n} tranches = +{units} sur le plafond",
			notWholeBlocks: "Cette option se vend par tranches de {block}. Demandez-en un nombre entier.",
			raise:
				"Passer de {from} à {to} est facturé maintenant, au prorata de ce qui reste du cycle, et prend effet au même instant.",
			lowerAt:
				"Descendre de {from} à {to} prend effet le {date}. D'ici là la limite reste celle d'aujourd'hui et le montant du mois ne change pas.",
			lowerEndOfCycle:
				"Descendre de {from} à {to} prend effet à la fin du cycle. D'ici là la limite reste celle d'aujourd'hui et le montant du mois ne change pas.",
			notSold:
				"Cette option n'est pas vendue ici. Parlez à qui administre l'installation pour relever la limite.",
			unlimited: "Illimité dans ce forfait",
			unlimitedUsed: "{n} en cours · aucun plafond du forfait",
			offer: {
				title: "Limite du forfait atteinte",
				usage: "{used} sur {limit} du forfait en cours d'utilisation.",
				extrasLead: "Les options se vendent par blocs, au prix de votre forfait.",
				extrasAction: "Augmenter la limite",
				planLead: "Les options arrivent avec un forfait payant.",
				planAction: "Choisir un forfait",
				noneAction: "Voir les forfaits",
			},
			monthly: "options par mois",
			discard: "Abandonner",
			save: "Enregistrer les options",
			saved:
				"Options enregistrées. La hausse s'applique déjà ; la baisse arrive au changement de cycle.",
			failed: "Impossible d'enregistrer les options. Rien n'a été facturé et rien n'a changé.",
		},

		invoice: {
			period: "{from} au {to}",
			paidOn: "Payée le {date}",
			dueOn: "Échéance le {date}",
			noDue: "Aucune échéance enregistrée",
			cardPack: "Pack de crédits · {credits} crédits · payé par carte",
			receipt: "Reçu",
			receiptMissing: "La passerelle de paiement n’a renvoyé aucun reçu pour ce débit.",
			lines: {
				description: "description",
				quantity: "qté",
				unit: "unitaire",
				amount: "montant",
				subtotal: "sous-total",
			},
			pending: {
				overdueTitle: "Facture en retard",
				openTitle: "Facture en attente",
				head: "{title} — {amount}, {month}",
				dueOn: "Échéance le {date}.",
				noDue: "Aucune date d'échéance enregistrée.",
				gatewayOn: "Le prélèvement est tenté automatiquement sur la carte enregistrée.",
				gatewayOff:
					"Le paiement automatique n'est pas activé ici : cette facture est payée en dehors et pointée comme payée par qui administre l'installation.",
				see: "Voir la facture",
			},
			preview: {
				title: "Aperçu de la prochaine facture",
				empty: "Rien à facturer sur ce cycle pour l'instant.",
				total: "total prévu",
			},
		},

		consumption: {
			from: "du",
			to: "au",
			allSources: "toutes les sources",
			rangeInverted:
				"La date de fin précède la date de début. La liste garde la dernière période valide.",
			sourceName: {
				panel: "panneau",
				channel: "canal",
				mission: "mission",
				automation: "automatisation",
			},
			calls: "réponses",
			credits: "crédits",
			avgDuration: "durée moyenne",
			errors: "erreurs",
			when: "quand",
			source: "origine",
			agent: "agent",
			duration: "durée",
			state: "état",
			failed: "erreur",
			ok: "ok",
			failedDetail: "Cette réponse a échoué.",
			turn: "voir cette réponse en entier",
			noDetail: "Aucun détail enregistré pour cette réponse.",
			toolsTitle: "Outils facturés dans ce tour",
			toolsLine: "{calls} × {rate} crédits",
			toolsShare: "{n} crédit en outils | {n} crédits en outils",
			empty: "Aucune réponse IA sur cette période avec ces filtres.",
		},

		admin: {
			courtesy: {
				title: "Gratuité",
				endsOn: "Prend fin au renouvellement du {date}",
				endless: "Sans date de fin",
				card: "Carte enregistrée : le premier prélèvement au plein tarif a lieu le {date}.",
				noCard: "Pas encore de carte : si le client ne s'abonne pas, l'espace expire le {date}.",
				date: "Gratuité jusqu'au",
				dateHint: "Prend fin au renouvellement qui tombe à cette date ou après.",
				save: "Enregistrer la date",
				clear: "Retirer la date de fin",
				failed: "Impossible d'enregistrer la date de fin de la gratuité.",
			},
			usage: {
				title: "Forfait et usage",
				subtitle:
					"Ce que l'espace utilise face à ce que son forfait permet — les mêmes chiffres que son écran de limites.",
				plan: "Forfait {plan} · {credits} crédits par mois",
				none: "Cet espace n'a pas de forfait.",
				unlimited: "illimité",
			},
			margin: {
				title: "Coût contre revenu, par espace",
				subtitle:
					"Ce que chaque espace paie, à côté de ce que son IA a coûté à la maison sur les 30 derniers jours.",
				workspace: "Espace",
				plan: "Offre",
				charged: "Facturé",
				credits: "Crédits",
				aiCost: "Coût IA (30j)",
				noPlan: "sans offre",
				empty: "Aucun espace à comparer pour l'instant.",
				note: "Le coût de l'IA n'est pas le seul coût, et l'abonnement n'est pas le seul revenu — les packs et les extras restent hors de ce tableau. Les deux colonnes sont de plus dans des devises différentes et ne sont jamais additionnées : aucun taux de change n'est conservé ici. Un mauvais rapport est une raison d'enquêter, pas un verdict.",
				error: "Impossible de charger la comparaison. Rechargez la page.",
			},
			kpis: {
				workspaces: "espaces de travail",
				onRail: "sur la voie {rail}",
				invoiced: "facturé en {month}",
				received: "encaissé",
				open: "en attente",
				overdue: "en retard",
				note: "Un espace de travail sain est sur exactement une voie — c'est une clé primaire dans la base de données, pas une promesse de cet écran.",
				rule: "Facturé = factures émises dans le mois + packs de crédits débités sur la carte dans le mois. Encaissé = ce qui a été payé dans le mois, quelle que soit la facture. En attente et en retard = tout ce qui reste impayé maintenant. Un débit refusé, ou qui attend encore le 3-D Secure, ne compte pas.",
			},
			table: {
				searchPlaceholder: "rechercher un espace de travail",
				allRails: "toutes les voies",
				allPlans: "tous les forfaits",
				workspace: "espace de travail",
				plan: "forfait",
				rail: "voie",
				nextCharge: "prochain prélèvement",
				pending: "en attente",
				extrasMonthly: "options/mois",
				packs: "packs",
				monthlyTotal: "total/mois",
				billedMonth: "facturé ce mois",
				cardPacks: "{count} par carte",
				credits: "crédits",
				closeCycle: "Clôturer le cycle",
				empty: "Aucun espace de travail ne correspond à ces filtres.",
				alreadyClosed:
					"Le cycle de {name} était déjà clôturé ce mois-ci — rien n'a été facturé à nouveau.",
				closed: "Cycle de {name} clôturé.",
				closedWithMonth: "Cycle de {name} clôturé ({month}).",
				closeFailed: "Impossible de clôturer le cycle de {name}. Rien n'a été facturé.",
				closeFailedWhy: "Impossible de clôturer le cycle de {name}. {why} Rien n'a été facturé.",
			},
			tenant: {
				title: "Facturation",
				subtitle: "forfait, crédits, options et la prochaine facture",
				none: "Cet espace de travail n'a pas de facturation configurée.",
				planCredits: "crédits du forfait",
				bonus: "bonus",
				cost: "coût réel du cycle",
				costHint: "ce que les fournisseurs ont facturé",
				margin: "marge",
				marginHint: "jamais montrée au client",
				plan: "forfait",
				noPlan: "sans forfait",
				switchNow: "Changer maintenant",
				switchNote:
					"Le changement prend effet immédiatement : les crédits du nouveau forfait arrivent et le montant récurrent change.",
				invoices: {
					title: "Historique des paiements",
					subtitle:
						"toutes les factures reçues par cet espace de travail, et comment chacune s'est terminée",
					month: "mois",
					total: "montant",
					status: "état",
					paidAt: "payée le",
					method: "moyen",
					externalRef: "référence externe",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "carte (Stripe)" },
					none: "Aucune facture n'a encore été émise pour cet espace de travail.",
				},
			},
			creditActions: {
				title: "Crédits, à la main",
				delta: "crédits (±)",
				deltaPlaceholder: "500",
				pool: "poche",
				poolPlan: "la poche du forfait (perdue au changement)",
				poolBonus: "bonus (n'expire pas)",
				reason: "motif",
				reasonPlaceholder: "reste dans le grand livre",
				post: "Passer l'ajustement",
				needAmount: "Dites combien de crédits, avec un signe — le négatif débite.",
				needReason: "Le motif reste dans le grand livre. Il est obligatoire.",
				posted: "Ajustement passé dans le grand livre.",
				adjustFailed: "Impossible de passer l'ajustement. Le solde n'a pas changé.",
				blockReason: "motif du blocage",
				needBlockReason:
					"Un blocage sans motif est un blocage que personne ne saura expliquer plus tard.",
				blocked:
					"IA bloquée par décision de la plateforme{reason}. Recharger des crédits ne le lève pas.",
				block: "Bloquer l'IA",
				unblock: "Débloquer l'IA",
				doneBlocked: "IA bloquée pour cet espace de travail.",
				doneUnblocked: "Blocage levé.",
				blockFailed: "Impossible de changer le blocage.",
			},
			extras: {
				resource: "ressource",
				invoiced: "facturé",
				waived: "offert",
				unit: "unitaire",
				subtotal: "sous-total",
				invoicedField: "Option facturée pour {resource}",
				waivedField: "Option offerte pour {resource}",
				scheduled: "{resource} : réduction à {quantity} programmée pour le {date}.",
				cancelSchedule: "Annuler la programmation",
				save: "Enregistrer (facture au prorata)",
				discard: "Abandonner",
				notForSale: "hors vente",
				perBlock: "par {size}",
				total: "total des options",
				failed: "Impossible d'enregistrer les options. Rien n'a été facturé.",
			},
		},

		pagination: {
			nav: "Pagination",
			of: "sur",
			perPage: "Par page",
			previous: "Précédent",
			next: "Suivant",
			page: "Page",
			goToPage: "Aller à la page",
			goTo: "Aller à",
		},
	},

	it: {
		waiver: {
			until: "Piano in omaggio fino al {date}",
			untilSetupHint:
				"Configura il pagamento prima del {date} per mantenere il piano. Nulla viene addebitato fino ad allora: il primo addebito è il {date}.",
			untilConfigured: "Pagamento configurato. Il primo addebito è il {date}.",
			label: "Abbonamento gratuito per questo piano",
			hint: "Mantiene il piano selezionato e i suoi limiti. I nuovi acquisti vengono addebitati separatamente.",
			active: "Abbonamento al piano gratuito",
			setup: "Configura pagamento",
			setupHint:
				"Il tuo piano resta gratuito. Aggiungi un metodo di pagamento su Stripe per acquistare extra o crediti.",
			failed: "Impossibile aprire la configurazione del pagamento.",
			free: "Piano gratuito",
			freeSetupHint:
				"Usalo senza carta. Aggiungine una quando vuoi acquistare crediti o componenti aggiuntivi — ora non viene addebitato nulla.",
		},
		nav: {
			aria: "Sezioni di fatturazione",
			plan: "Il mio piano",
			credits: "Crediti",
			limits: "Limiti",
			invoices: "Fatture",
			consumption: "Consumo",
		},

		resource: {
			users: {
				label: "utenti",
				hint: "persone con accesso a questo spazio di lavoro",
				amount: "{n} utente | {n} utenti",
				quantity: "{n}",
			},
			channels: {
				label: "canali",
				hint: "numeri, caselle e chat collegati",
				amount: "{n} canale | {n} canali",
				quantity: "{n}",
			},
			storage_gb: {
				label: "archiviazione",
				hint: "file, media e allegati conservati",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "contatti attivi",
				hint: "persone distinte servite nel mese",
				amount: "{n} contatto attivo | {n} contatti attivi",
				quantity: "{n}",
			},
		},

		rail: {
			none: "senza fatturazione",
			gateway: { label: "carta", hint: "addebitato automaticamente sulla carta" },
			free: { label: "gratuito", hint: "non viene addebitato" },
			courtesy: { label: "omaggio", hint: "non viene addebitato" },
			trial: { label: "prova", hint: "nel periodo di prova gratuita — ancora nessun addebito" },
		},

		subscriptionStatus: {
			active: "attivo",
			trialing: "in prova",
			past_due: "in ritardo",
			canceled: "disdetto",
			incomplete: "incompleto",
			expired: "scaduta",
		},
		invoiceStatus: { open: "in sospeso", paid: "pagata", overdue: "scaduta", void: "annullata" },
		ledgerKind: {
			consumption: "consumo",
			debit: "consumo",
			refill: "rinnovo del ciclo",
			plan_change: "cambio di piano",
			trial_end: "fine della prova",
			plan_end: "fine del piano",
			bonus: "bonus",
			pack: "pacchetto acquistato",
			adjustment: "rettifica",
			reversal: "storno",
			expiry: "scadenza",
		},

		plan: {
			card: {
				current: "Piano attuale",
				select: "Seleziona",
				upgrade: "Passa a superiore",
				downgrade: "Passa a inferiore",
				switch: "Cambia",
				featured: "in evidenza",
				perMonth: "/mese",
				creditsPerMonth: "{credits} crediti al mese",
				smartAnswers: "≈ {n} risposte",
				smartAnswersRecent: "≈ {n} risposte, in base all'uso recente",
				noLimit: "senza limite",
				users: "utenti",
				channels: "canali",
				storage: "archiviazione",
				contacts: "contatti/mese",
				units: "workspace",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "Non è stato possibile leggere il catalogo dei piani",
				errorHint: "È un guasto nostro, non una mancanza di piani. Riprova tra poco.",
				emptyTitle: "Nessun piano pubblicato",
				emptyHint: "Questa installazione non ha ancora pubblicato un catalogo commerciale.",
				failed: "Non è stato possibile cambiare piano ora. Non è stato addebitato nulla.",
				noGateway:
					"I piani a pagamento si addebitano sulla carta, e il pagamento con carta non è attivo in questa installazione — un piano a pagamento non si può scegliere qui finché non lo è.",
			},
			cycle: {
				title: "Il mio piano",
				creditsPerMonth: "{credits} crediti al mese",
				trialCredits: "{credits} crediti per provare",
				monthly: "piano",
				extras: "extra",
				total: "totale al mese",
				nextCharge: "prossimo addebito",
				noNextCharge: "nessuno previsto",
				trialEnds: "fine della prova",
				trialDaysLeft: "termina oggi | manca {n} giorno | mancano {n} giorni",
				yearly:
					"Piano annuale: {price} all'anno, si rinnova il {date}. I crediti si ricaricano ogni mese.",
				anchor: "ogni mese il giorno {day}",
				window: "Ciclo dal {from} al {to}, {anchor} — {rail}.",
				trialWindow: "Prova gratuita fino al {date}: nulla viene addebitato fino ad allora.",
				noSubscription:
					"Questo piano non ha ancora un abbonamento: non viene addebitato nulla in automatico.",
				noPlan: "Questo spazio di lavoro non ha ancora un piano.",
				noPlanHint:
					"Puoi usare tutto il pannello senza — quello che non puoi fare è chiamare l'IA. Scegli un piano qui sotto e i crediti entrano nello stesso istante, oppure",
				noPlanLink: "guarda la griglia senza il pannello intorno",
			},
			subscription: {
				title: "Abbonamento",
				scheduledTitle: "Abbonamento disdetto per la fine del ciclo.",
				scheduledBody:
					"Il piano resta valido fino al {date} e dopo non viene addebitato nulla. I crediti del piano smettono di rinnovarsi; il saldo bonus non scade.",
				reactivate: "Riattiva l'abbonamento",
				noGateway:
					"Il pagamento automatico non è attivo in questa installazione. I piani a pagamento si addebitano solo sulla carta, quindi qui non si addebita nulla finché non lo è — in Fatture vedi comunque tutte le fatture già emesse.",
				activate: "Attiva il pagamento mensile",
				cancel: "Disdici l'abbonamento",
				cancelTitle: "Disdire l'abbonamento?",
				cancelDescription: "La disdetta vale alla fine del ciclo — oggi non si ferma nulla.",
				cancelBody1:
					"Il piano resta valido fino al {date}. Dopo, il rinnovo dei crediti si ferma e l'IA smette di rispondere; il pannello, le conversazioni e i record restano accessibili.",
				cancelBody2:
					"Puoi riattivarlo in qualsiasi momento prima di quella data, da questa stessa schermata.",
				keep: "Mantieni l'abbonamento",
				confirmCancel: "Disdici alla fine del ciclo",
				cancelFailed: "Non è stato possibile programmare la disdetta. Riprova tra poco.",
				reactivateFailed: "Non è stato possibile riattivare l'abbonamento.",
				checkoutFailed: "Non è stato possibile aprire il pagamento ora.",
			},
		},

		credits: {
			chip: {
				credits: "{n} crediti",
				detail: "{plan} dal piano e {bonus} di bonus.",
				renews: "Si rinnova il {date}.",
				paused: "IA in pausa",
				pausedDetail:
					"Piano e bonus sono a zero, quindi l'IA ha smesso di rispondere. Un pacchetto di crediti la sblocca subito.",
				blocked: "IA bloccata",
				blockedDetail:
					"Blocco di chi amministra l'installazione. Comprare crediti non lo annulla — parla con l'assistenza.",
			},
			meters: {
				title: "Crediti",
				blockedTitle: "IA bloccata in questo spazio di lavoro.",
				blockedByAdmin:
					"Il blocco è stato messo da chi amministra l'installazione{reason}. Comprare crediti NON lo annulla — parla con l'assistenza.",
				reasonQuote: ": “{reason}”",
				blockedNoCredits:
					"Il saldo è arrivato a zero. Un pacchetto di crediti sblocca subito le risposte, e il rinnovo del piano entra da solo il {date}.",
				planLabel: "crediti del piano",
				percentUsed: "{percent}% del ciclo consumato",
				renews: "si rinnova il {date}",
				noRenewal: "nessuna data di rinnovo registrata",
				bonus: "bonus",
				bonusHint: "non scade",
				total: "totale disponibile",
				totalHint: "il piano si consuma per primo",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "crediti di prova",
				trialUntil:
					"Validi fino al {date}. Quando sottoscrivi un piano, quello che resta dei crediti di benvenuto della prova resta a te e si somma ai crediti del piano.",
			},
			packs: {
				title: "Compra crediti",
				subtitle: "addebitati subito sulla tua carta; i crediti entrano appena dopo",
				noPlan: "I pacchetti singoli sono per chi ha già un piano.",
				noPlanHint:
					"Un pacchetto viene addebitato sulla fattura del mese, e la fattura nasce dal ciclo del piano. Scegli prima un piano — i suoi crediti mensili potrebbero già bastare.",
				seePlans: "Vedi i piani",
				empty: "Questa installazione non ha pubblicato pacchetti singoli.",
				credits: "{credits} crediti",
				perThousand: "{price} ogni 1.000 crediti",
				buy: "Compra",
				bought: "{name} acquistato. I crediti sono già nel saldo bonus.",
				failed: "Non è stato possibile comprare il pacchetto. Non è stato addebitato nulla.",
				confirmTitle: "Conferma l'acquisto",
				confirmDescription:
					"La tua carta salvata viene addebitata ora. I crediti entrano appena l'addebito passa.",
				confirmBody:
					"— {credits} crediti per {price}, addebitati ora sulla tua carta salvata. Vanno nel saldo bonus, che non scade. Se la carta viene rifiutata, non viene addebitato nulla e non entrano crediti.",
				trialLocked: "Scegli un piano per acquistare crediti singoli.",
				cardLocked:
					"I pacchetti si addebitano subito sulla carta di un piano a pagamento, e questo spazio di lavoro non ha una carta da addebitare.",
				notNow: "Non ora",
			},
			ledger: {
				title: "Movimenti dei crediti",
				subtitle: "rinnovo, cambio di piano, bonus, pacchetto, rettifica, storno",
				allKinds: "tutti i tipi",
				includeConsumption: "Includi il consumo nell'elenco",
				when: "quando",
				kind: "tipo",
				pool: "origine",
				credits: "crediti",
				planAfter: "saldo del piano dopo",
				bonusAfter: "saldo bonus dopo",
				reason: "motivo",
				welcome: "Crediti di benvenuto",
				welcomeTrial: "Crediti di benvenuto della prova",
				poolPlan: "piano",
				poolBonus: "bonus",
				empty: "Nessun movimento di credito in questo periodo.",
			},
		},

		limits: {
			meter: "{resource} in uso",
			fromPlan: "{n} dal piano",
			fromExtra: "+ {n} come extra",
			waived: "+ {n} in omaggio (non fatturati)",
			percentUsed: "{percent}% usato",
			window: "Finestra del ciclo: da {start} a {end}.",
			notEnforced:
				"Questo numero viene monitorato e in questa versione non blocca ancora nulla: superare il limite non interrompe il servizio.",
			enforcedContacts:
				"I nuovi contatti oltre il limite scrivono e i messaggi sono conservati, ma nessuno li gestisce finché non si acquistano altri contatti o non cambia il ciclo.",
			enforcedStorage:
				"Con lo spazio pieno, i caricamenti del team vengono rifiutati. I messaggi inviati dai clienti continuano ad arrivare e sono conservati.",
			scheduled: "Riduzione a {quantity} programmata per il {date}.",
			addonField: "Extra per {resource}",
			unitPrice: "{price} per unità/mese · {total} con quello che c'è nel campo",
			blockPrice: "{price} ogni {block}/mese · {total} con quello che c'è nel campo",
			blockField: "Blocchi da {block}",
			blockCeiling: "{n} blocco = +{units} sul tetto | {n} blocchi = +{units} sul tetto",
			notWholeBlocks: "Questo componente si vende a blocchi da {block}. Chiedine un numero intero.",
			raise:
				"Salire da {from} a {to} viene addebitato ora, in proporzione a quanto resta del ciclo, e vale dallo stesso istante.",
			lowerAt:
				"Scendere da {from} a {to} vale dal {date}. Fino ad allora il limite resta quello di oggi e l'importo del mese non cambia.",
			lowerEndOfCycle:
				"Scendere da {from} a {to} vale alla fine del ciclo. Fino ad allora il limite resta quello di oggi e l'importo del mese non cambia.",
			notSold:
				"Questo extra non si vende qui. Parla con chi amministra l'installazione per alzare il limite.",
			unlimited: "Illimitato in questo piano",
			unlimitedUsed: "{n} in uso · nessun tetto dal piano",
			offer: {
				title: "Limite del piano raggiunto",
				usage: "{used} di {limit} del piano in uso.",
				extrasLead: "Gli extra si vendono a blocchi, al prezzo del tuo piano.",
				extrasAction: "Alza il limite",
				planLead: "Gli extra arrivano con un piano a pagamento.",
				planAction: "Scegli un piano",
				noneAction: "Vedi i piani",
			},
			monthly: "extra al mese",
			discard: "Scarta",
			save: "Salva gli extra",
			saved: "Extra salvati. L'aumento vale già; la riduzione entra al cambio di ciclo.",
			failed:
				"Non è stato possibile salvare gli extra. Non è stato addebitato nulla e non è cambiato nulla.",
		},

		invoice: {
			period: "dal {from} al {to}",
			paidOn: "Pagata il {date}",
			dueOn: "Scade il {date}",
			noDue: "Nessuna scadenza registrata",
			cardPack: "Pacchetto di crediti · {credits} crediti · pagato con carta",
			receipt: "Ricevuta",
			receiptMissing: "Il gateway di pagamento non ha restituito una ricevuta per questo addebito.",
			lines: {
				description: "descrizione",
				quantity: "qtà",
				unit: "unitario",
				amount: "importo",
				subtotal: "subtotale",
			},
			pending: {
				overdueTitle: "Fattura scaduta",
				openTitle: "Fattura in sospeso",
				head: "{title} — {amount}, {month}",
				dueOn: "Scade il {date}.",
				noDue: "Nessuna data di scadenza registrata.",
				gatewayOn: "L'addebito viene tentato automaticamente sulla carta registrata.",
				gatewayOff:
					"Il pagamento automatico non è attivo qui: questa fattura si paga fuori e viene segnata come pagata da chi amministra l'installazione.",
				see: "Vedi la fattura",
			},
			preview: {
				title: "Anteprima della prossima fattura",
				empty: "Niente da fatturare in questo ciclo finora.",
				total: "totale previsto",
			},
		},

		consumption: {
			from: "dal",
			to: "al",
			allSources: "tutte le origini",
			rangeInverted:
				"La data finale è prima di quella iniziale. L'elenco resta sull'ultimo periodo valido.",
			sourceName: {
				panel: "pannello",
				channel: "canale",
				mission: "missione",
				automation: "automazione",
			},
			calls: "risposte",
			credits: "crediti",
			avgDuration: "durata media",
			errors: "errori",
			when: "quando",
			source: "origine",
			agent: "agente",
			duration: "durata",
			state: "stato",
			failed: "errore",
			ok: "ok",
			failedDetail: "Questa risposta è fallita.",
			turn: "vedi la risposta completa",
			noDetail: "Nessun dettaglio registrato per questa risposta.",
			toolsTitle: "Strumenti addebitati in questo turno",
			toolsLine: "{calls} × {rate} crediti",
			toolsShare: "{n} credito in strumenti | {n} crediti in strumenti",
			empty: "Nessuna risposta IA in questo periodo con questi filtri.",
		},

		admin: {
			courtesy: {
				title: "Omaggio",
				endsOn: "Termina al rinnovo del {date}",
				endless: "Senza data di fine",
				card: "Carta registrata: il primo addebito a prezzo pieno è il {date}.",
				noCard: "Ancora senza carta: se il cliente non si abbona, lo spazio scade il {date}.",
				date: "Omaggio fino al",
				dateHint: "Termina al rinnovo che cade in questa data o dopo.",
				save: "Salva data",
				clear: "Rimuovi data di fine",
				failed: "Impossibile salvare la data di fine dell'omaggio.",
			},
			usage: {
				title: "Piano e utilizzo",
				subtitle:
					"Quanto lo spazio usa rispetto a quanto il piano consente: gli stessi numeri della sua schermata dei limiti.",
				plan: "Piano {plan} · {credits} crediti al mese",
				none: "Questo spazio non ha un piano.",
				unlimited: "illimitato",
			},
			margin: {
				title: "Costo contro ricavo, per spazio",
				subtitle:
					"Quanto paga ogni spazio, accanto a quanto la sua IA è costata a questa casa negli ultimi 30 giorni.",
				workspace: "Spazio",
				plan: "Piano",
				charged: "Addebitato",
				credits: "Crediti",
				aiCost: "Costo IA (30g)",
				noPlan: "senza piano",
				empty: "Nessuno spazio da confrontare per ora.",
				note: "Il costo dell'IA non è l'unico costo, e il canone non è l'unico ricavo — pacchetti ed extra restano fuori da questa tabella. Le due colonne sono inoltre in valute diverse e non vengono mai sommate: qui non si conserva alcun cambio. Un rapporto brutto è un motivo per indagare, non un verdetto.",
				error: "Impossibile caricare il confronto. Ricarica la pagina.",
			},
			kpis: {
				workspaces: "spazi di lavoro",
				onRail: "sul binario {rail}",
				invoiced: "fatturato a {month}",
				received: "incassato",
				open: "in sospeso",
				overdue: "scaduto",
				note: "Uno spazio di lavoro sano sta su esattamente un binario — è una chiave primaria nel database, non una promessa di questa schermata.",
				rule: "Fatturato = fatture emesse nel mese + pacchetti di crediti addebitati sulla carta nel mese. Incassato = ciò che è stato pagato nel mese, da qualsiasi fattura. Aperto e scaduto = tutto ciò che è ancora da pagare adesso. Un addebito rifiutato, o ancora in attesa del 3-D Secure, non conta.",
			},
			table: {
				searchPlaceholder: "cerca spazio di lavoro",
				allRails: "tutti i binari",
				allPlans: "tutti i piani",
				workspace: "spazio di lavoro",
				plan: "piano",
				rail: "binario",
				nextCharge: "prossimo addebito",
				pending: "in sospeso",
				extrasMonthly: "extra/mese",
				packs: "pacchetti",
				monthlyTotal: "totale/mese",
				billedMonth: "fatturato nel mese",
				cardPacks: "{count} con carta",
				credits: "crediti",
				closeCycle: "Chiudi ciclo",
				empty: "Nessuno spazio di lavoro con questi filtri.",
				alreadyClosed:
					"Il ciclo di {name} era già chiuso questo mese — non è stato addebitato nulla di nuovo.",
				closed: "Ciclo di {name} chiuso.",
				closedWithMonth: "Ciclo di {name} chiuso ({month}).",
				closeFailed:
					"Non è stato possibile chiudere il ciclo di {name}. Non è stato addebitato nulla.",
				closeFailedWhy:
					"Non è stato possibile chiudere il ciclo di {name}. {why} Non è stato addebitato nulla.",
			},
			tenant: {
				title: "Fatturazione",
				subtitle: "piano, crediti, extra e la prossima fattura",
				none: "Questo spazio di lavoro non ha la fatturazione configurata.",
				planCredits: "crediti del piano",
				bonus: "bonus",
				cost: "costo reale del ciclo",
				costHint: "quanto hanno addebitato i fornitori",
				margin: "margine",
				marginHint: "non viene mai mostrato al cliente",
				plan: "piano",
				noPlan: "senza piano",
				switchNow: "Cambia ora",
				switchNote:
					"Il cambio vale subito: entrano i crediti del nuovo piano e l'importo ricorrente cambia.",
				invoices: {
					title: "Storico dei pagamenti",
					subtitle: "tutte le fatture ricevute da questo spazio di lavoro, e come è finita ognuna",
					month: "mese",
					total: "importo",
					status: "stato",
					paidAt: "pagata il",
					method: "modalità",
					externalRef: "riferimento esterno",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "carta (Stripe)" },
					none: "Non è ancora stata emessa nessuna fattura per questo spazio di lavoro.",
				},
			},
			creditActions: {
				title: "Crediti, a mano",
				delta: "crediti (±)",
				deltaPlaceholder: "500",
				pool: "tasca",
				poolPlan: "la tasca del piano (sparisce al cambio)",
				poolBonus: "bonus (non scade)",
				reason: "motivo",
				reasonPlaceholder: "resta nel libro mastro",
				post: "Registra rettifica",
				needAmount: "Indica quanti crediti, con il segno — il negativo addebita.",
				needReason: "Il motivo resta nel libro mastro. È obbligatorio.",
				posted: "Rettifica registrata nel libro mastro.",
				adjustFailed: "Non è stato possibile registrare la rettifica. Il saldo non è cambiato.",
				blockReason: "motivo del blocco",
				needBlockReason: "Un blocco senza motivo è un blocco che poi nessuno sa spiegare.",
				blocked:
					"IA bloccata per decisione della piattaforma{reason}. Ricaricare crediti non lo annulla.",
				block: "Blocca IA",
				unblock: "Sblocca IA",
				doneBlocked: "IA bloccata per questo spazio di lavoro.",
				doneUnblocked: "Blocco rimosso.",
				blockFailed: "Non è stato possibile cambiare il blocco.",
			},
			extras: {
				resource: "risorsa",
				invoiced: "fatturato",
				waived: "in omaggio",
				unit: "unitario",
				subtotal: "subtotale",
				invoicedField: "Extra fatturato per {resource}",
				waivedField: "Extra in omaggio per {resource}",
				scheduled: "{resource}: riduzione a {quantity} programmata per il {date}.",
				cancelSchedule: "Annulla la programmazione",
				save: "Salva (addebita in proporzione)",
				discard: "Scarta",
				notForSale: "non in vendita",
				perBlock: "ogni {size}",
				total: "totale extra",
				failed: "Non è stato possibile salvare gli extra. Non è stato addebitato nulla.",
			},
		},

		pagination: {
			nav: "Impaginazione",
			of: "di",
			perPage: "Per pagina",
			previous: "Precedente",
			next: "Successiva",
			page: "Pagina",
			goToPage: "Vai alla pagina",
			goTo: "Vai a",
		},
	},

	de: {
		waiver: {
			until: "Tarif kostenlos bis {date}",
			untilSetupHint:
				"Richten Sie die Zahlung vor dem {date} ein, um Ihren Tarif zu behalten. Bis dahin wird nichts berechnet – die erste Abbuchung erfolgt am {date}.",
			untilConfigured: "Zahlung eingerichtet. Die erste Abbuchung erfolgt am {date}.",
			label: "Abonnementgebühr für diesen Tarif erlassen",
			hint: "Behält den gewählten Tarif und seine Limits bei. Neue Käufe werden separat berechnet.",
			active: "Abonnementgebühr erlassen",
			setup: "Zahlung einrichten",
			setupHint:
				"Ihr Tarif bleibt kostenlos. Hinterlegen Sie bei Stripe eine Zahlungsmethode, um Extras oder Guthaben zu kaufen.",
			failed: "Die Zahlungseinrichtung konnte nicht geöffnet werden.",
			free: "Kostenloser Tarif",
			freeSetupHint:
				"Ohne Karte nutzbar. Hinterlege eine, wenn du Credits oder Zusatzoptionen kaufen willst — jetzt wird nichts abgebucht.",
		},
		nav: {
			aria: "Abrechnungsbereiche",
			plan: "Mein Tarif",
			credits: "Credits",
			limits: "Limits",
			invoices: "Rechnungen",
			consumption: "Verbrauch",
		},

		resource: {
			users: {
				label: "Nutzer",
				hint: "Personen mit Zugang zu diesem Workspace",
				amount: "{n} Nutzer | {n} Nutzer",
				quantity: "{n}",
			},
			channels: {
				label: "Kanäle",
				hint: "verbundene Nummern, Postfächer und Chats",
				amount: "{n} Kanal | {n} Kanäle",
				quantity: "{n}",
			},
			storage_gb: {
				label: "Speicher",
				hint: "gespeicherte Dateien, Medien und Anhänge",
				amount: "{n} GB",
				quantity: "{n} GB",
			},
			contacts: {
				label: "aktive Kontakte",
				hint: "verschiedene Personen, die im Monat betreut wurden",
				amount: "{n} aktiver Kontakt | {n} aktive Kontakte",
				quantity: "{n}",
			},
		},

		rail: {
			none: "keine Abrechnung",
			gateway: { label: "Karte", hint: "wird automatisch von der Karte abgebucht" },
			free: { label: "kostenlos", hint: "wird nicht berechnet" },
			courtesy: { label: "Kulanz", hint: "wird nicht berechnet" },
			trial: { label: "Testphase", hint: "in der kostenlosen Testphase — noch keine Abrechnung" },
		},

		subscriptionStatus: {
			active: "aktiv",
			trialing: "im Test",
			past_due: "überfällig",
			canceled: "gekündigt",
			incomplete: "unvollständig",
			expired: "abgelaufen",
		},
		invoiceStatus: { open: "offen", paid: "bezahlt", overdue: "überfällig", void: "storniert" },
		ledgerKind: {
			consumption: "Verbrauch",
			debit: "Verbrauch",
			refill: "Zyklus-Erneuerung",
			plan_change: "Tarifwechsel",
			trial_end: "Testende",
			plan_end: "Tarifende",
			bonus: "Bonus",
			pack: "Paketkauf",
			adjustment: "Korrektur",
			reversal: "Storno",
			expiry: "Verfall",
		},

		plan: {
			card: {
				current: "Aktueller Tarif",
				select: "Auswählen",
				upgrade: "Höherstufen",
				downgrade: "Herabstufen",
				switch: "Wechseln",
				featured: "empfohlen",
				perMonth: "/Monat",
				creditsPerMonth: "{credits} Credits pro Monat",
				smartAnswers: "≈ {n} Antworten",
				smartAnswersRecent: "≈ {n} Antworten, nach Ihrer jüngsten Nutzung",
				noLimit: "kein Limit",
				users: "Nutzer",
				channels: "Kanäle",
				storage: "Speicher",
				contacts: "Kontakte/Mon.",
				units: "Workspaces",
				gb: "{n} GB",
			},
			grid: {
				errorTitle: "Der Tarifkatalog konnte nicht gelesen werden",
				errorHint:
					"Das ist ein Fehler auf unserer Seite, kein Mangel an Tarifen. Versuch es gleich noch einmal.",
				emptyTitle: "Kein Tarif veröffentlicht",
				emptyHint: "Diese Installation hat noch keinen kommerziellen Katalog veröffentlicht.",
				failed: "Der Tarif konnte gerade nicht gewechselt werden. Es wurde nichts berechnet.",
				noGateway:
					"Bezahlte Tarife werden per Karte abgerechnet, und Kartenzahlung ist in dieser Installation nicht eingeschaltet — ein bezahlter Tarif lässt sich hier erst wählen, wenn sie es ist.",
			},
			cycle: {
				title: "Mein Tarif",
				creditsPerMonth: "{credits} Credits pro Monat",
				trialCredits: "{credits} Credits zum Testen",
				monthly: "Tarif",
				extras: "Add-ons",
				total: "Gesamt pro Monat",
				nextCharge: "nächste Abbuchung",
				noNextCharge: "keine geplant",
				trialEnds: "Testende",
				trialDaysLeft: "endet heute | noch {n} Tag | noch {n} Tage",
				yearly:
					"Jahresplan: {price} pro Jahr, verlängert sich am {date}. Die Credits werden weiterhin monatlich aufgefüllt.",
				anchor: "monatlich am Tag {day}",
				window: "Zyklus vom {from} bis {to}, {anchor} — {rail}.",
				trialWindow: "Kostenloser Test bis {date}: bis dahin wird nichts berechnet.",
				noSubscription: "Dieser Tarif hat noch kein Abo: Es wird nichts automatisch abgebucht.",
				noPlan: "Dieser Workspace hat noch keinen Tarif.",
				noPlanHint:
					"Du kannst das ganze Panel ohne Tarif nutzen — nur die KI kannst du nicht aufrufen. Wähle unten einen Tarif und die Credits sind im selben Moment da, oder",
				noPlanLink: "sieh dir die Übersicht ohne das Panel drumherum an",
			},
			subscription: {
				title: "Abonnement",
				scheduledTitle: "Abo zum Ende des Zyklus gekündigt.",
				scheduledBody:
					"Der Tarif läuft bis {date} und danach wird nichts mehr abgebucht. Die Credits des Tarifs werden nicht mehr erneuert; das Bonus-Guthaben verfällt nicht.",
				reactivate: "Abo reaktivieren",
				noGateway:
					"Die automatische Zahlung ist in dieser Installation nicht eingeschaltet. Bezahlte Tarife werden nur per Karte abgerechnet, daher wird hier nichts berechnet, bis sie es ist — unter Rechnungen siehst du weiterhin alle bereits ausgestellten Rechnungen.",
				activate: "Monatliche Zahlung einschalten",
				cancel: "Abo kündigen",
				cancelTitle: "Das Abo kündigen?",
				cancelDescription: "Die Kündigung gilt zum Ende des Zyklus — heute hört nichts auf.",
				cancelBody1:
					"Der Tarif läuft bis {date}. Danach stoppt die Credit-Erneuerung und die KI antwortet nicht mehr; Panel, Konversationen und Datensätze bleiben erreichbar.",
				cancelBody2:
					"Du kannst jederzeit vor diesem Datum reaktivieren, von dieser selben Maske aus.",
				keep: "Abo behalten",
				confirmCancel: "Zum Ende des Zyklus kündigen",
				cancelFailed:
					"Die Kündigung konnte nicht eingeplant werden. Versuch es gleich noch einmal.",
				reactivateFailed: "Das Abo konnte nicht reaktiviert werden.",
				checkoutFailed: "Die Zahlung konnte gerade nicht geöffnet werden.",
			},
		},

		credits: {
			chip: {
				credits: "{n} Credits",
				detail: "{plan} aus dem Tarif und {bonus} als Bonus.",
				renews: "Erneuert sich am {date}.",
				paused: "KI pausiert",
				pausedDetail:
					"Tarif und Bonus stehen auf null, deshalb hat die KI aufgehört zu antworten. Ein Credit-Paket gibt sie sofort wieder frei.",
				blocked: "KI gesperrt",
				blockedDetail:
					"Sperre von der Verwaltung dieser Installation. Credits zu kaufen hebt sie nicht auf — sprich mit dem Support.",
			},
			meters: {
				title: "Credits",
				blockedTitle: "KI in diesem Workspace gesperrt.",
				blockedByAdmin:
					"Die Sperre wurde von der Verwaltung dieser Installation gesetzt{reason}. Credits zu kaufen hebt sie NICHT auf — sprich mit dem Support.",
				reasonQuote: ": „{reason}“",
				blockedNoCredits:
					"Das Guthaben ist bei null angekommen. Ein Credit-Paket gibt die Antworten sofort wieder frei, und die Erneuerung des Tarifs kommt am {date} von selbst.",
				planLabel: "Tarif-Credits",
				percentUsed: "{percent}% des Zyklus verbraucht",
				renews: "erneuert sich am {date}",
				noRenewal: "kein Erneuerungsdatum hinterlegt",
				bonus: "Bonus",
				bonusHint: "verfällt nicht",
				total: "insgesamt verfügbar",
				totalHint: "der Tarif wird zuerst verbraucht",
				/** The trial's welcome credits sit in the bonus pocket, which a new plan keeps (`assign-plan.ts`); only the plan pocket is replaced. */
				trialCredits: "Test-Credits",
				trialUntil:
					"Gültig bis {date}. Wenn Sie einen Tarif abschließen, bleibt Ihnen der Rest der Willkommens-Credits aus dem Test erhalten und kommt zu den Credits des Tarifs hinzu.",
			},
			packs: {
				title: "Credits kaufen",
				subtitle: "jetzt von Ihrer Karte abgebucht; die Credits kommen direkt danach",
				noPlan: "Einzelne Pakete sind für alle, die schon einen Tarif haben.",
				noPlanHint:
					"Ein Paket wird auf der Rechnung des Monats berechnet, und die Rechnung entsteht aus dem Zyklus des Tarifs. Wähle zuerst einen Tarif — seine monatlichen Credits reichen vielleicht schon.",
				seePlans: "Tarife ansehen",
				empty: "Diese Installation hat keine einzelnen Pakete veröffentlicht.",
				credits: "{credits} Credits",
				perThousand: "{price} pro 1.000 Credits",
				buy: "Kaufen",
				bought: "{name} gekauft. Die Credits sind schon im Bonus-Guthaben.",
				failed: "Das Paket konnte nicht gekauft werden. Es wurde nichts berechnet.",
				confirmTitle: "Kauf bestätigen",
				confirmDescription:
					"Ihre gespeicherte Karte wird jetzt belastet. Die Credits kommen, sobald die Zahlung durchgeht.",
				confirmBody:
					"— {credits} Credits für {price}, jetzt von Ihrer gespeicherten Karte abgebucht. Sie gehen ins Bonus-Guthaben, das nicht verfällt. Wird die Karte abgelehnt, wird nichts abgebucht und es kommen keine Credits an.",
				trialLocked: "Wählen Sie einen Tarif, um einzelne Credits zu kaufen.",
				cardLocked:
					"Pakete werden sofort über die Karte eines bezahlten Tarifs abgerechnet, und dieser Workspace hat keine Karte, die belastet werden kann.",
				notNow: "Jetzt nicht",
			},
			ledger: {
				title: "Credit-Buchungen",
				subtitle: "Erneuerung, Tarifwechsel, Bonus, Paket, Korrektur, Storno",
				allKinds: "alle Arten",
				includeConsumption: "Verbrauch in der Liste anzeigen",
				when: "wann",
				kind: "Art",
				pool: "Herkunft",
				credits: "Credits",
				planAfter: "Tarifguthaben danach",
				bonusAfter: "Bonusguthaben danach",
				reason: "Grund",
				welcome: "Willkommensguthaben",
				welcomeTrial: "Willkommensguthaben der Testphase",
				poolPlan: "Tarif",
				poolBonus: "Bonus",
				empty: "Keine Credit-Buchung in diesem Zeitraum.",
			},
		},

		limits: {
			meter: "{resource} in Nutzung",
			fromPlan: "{n} aus dem Tarif",
			fromExtra: "+ {n} als Add-on",
			waived: "+ {n} erlassen (nicht berechnet)",
			percentUsed: "{percent}% genutzt",
			window: "Abrechnungszeitraum: {start} bis {end}.",
			notEnforced:
				"Diese Zahl wird mitgezählt und blockiert in dieser Version noch nichts: Über dem Limit wird der Betrieb nicht unterbrochen.",
			enforcedContacts:
				"Neue Kontakte über dem Limit schreiben und ihre Nachrichten bleiben gespeichert, aber niemand bearbeitet sie, bis mehr Kontakte gekauft sind oder der Zyklus wechselt.",
			enforcedStorage:
				"Bei vollem Speicher werden Uploads des Teams abgelehnt. Nachrichten deiner Kunden kommen weiter an und bleiben gespeichert.",
			scheduled: "Senkung auf {quantity} für den {date} eingeplant.",
			addonField: "Add-on für {resource}",
			unitPrice: "{price} pro Einheit/Monat · {total} mit dem, was im Feld steht",
			blockPrice: "{price} je {block}/Monat · {total} mit dem, was im Feld steht",
			blockField: "Blöcke von {block}",
			blockCeiling: "{n} Block = +{units} beim Limit | {n} Blöcke = +{units} beim Limit",
			notWholeBlocks:
				"Diese Erweiterung wird in Blöcken von {block} verkauft. Bitte eine ganze Zahl davon angeben.",
			raise:
				"Die Erhöhung von {from} auf {to} wird jetzt anteilig für den Rest des Zyklus berechnet und gilt im selben Moment.",
			lowerAt:
				"Die Senkung von {from} auf {to} gilt ab dem {date}. Bis dahin bleibt das Limit wie heute und der Betrag des Monats ändert sich nicht.",
			lowerEndOfCycle:
				"Die Senkung von {from} auf {to} gilt zum Ende des Zyklus. Bis dahin bleibt das Limit wie heute und der Betrag des Monats ändert sich nicht.",
			notSold:
				"Dieses Add-on wird hier nicht verkauft. Sprich mit der Verwaltung dieser Installation, um das Limit zu erhöhen.",
			unlimited: "In diesem Tarif unbegrenzt",
			unlimitedUsed: "{n} in Nutzung · kein Limit aus dem Tarif",
			offer: {
				title: "Tariflimit erreicht",
				usage: "{used} von {limit} des Tarifs in Nutzung.",
				extrasLead: "Add-ons werden in Blöcken zum Preis deines Tarifs verkauft.",
				extrasAction: "Limit erhöhen",
				planLead: "Add-ons gibt es mit einem bezahlten Tarif.",
				planAction: "Tarif wählen",
				noneAction: "Tarife ansehen",
			},
			monthly: "Add-ons pro Monat",
			discard: "Verwerfen",
			save: "Add-ons speichern",
			saved: "Add-ons gespeichert. Die Erhöhung gilt bereits; die Senkung kommt zum Zykluswechsel.",
			failed:
				"Die Add-ons konnten nicht gespeichert werden. Es wurde nichts berechnet und nichts geändert.",
		},

		invoice: {
			period: "{from} bis {to}",
			paidOn: "Bezahlt am {date}",
			dueOn: "Fällig am {date}",
			noDue: "Keine Fälligkeit hinterlegt",
			cardPack: "Guthabenpaket · {credits} Credits · per Karte bezahlt",
			receipt: "Beleg",
			receiptMissing: "Das Zahlungsgateway hat für diese Abbuchung keinen Beleg geliefert.",
			lines: {
				description: "Beschreibung",
				quantity: "Menge",
				unit: "Einzelpreis",
				amount: "Betrag",
				subtotal: "Zwischensumme",
			},
			pending: {
				overdueTitle: "Überfällige Rechnung",
				openTitle: "Offene Rechnung",
				head: "{title} — {amount}, {month}",
				dueOn: "Fällig am {date}.",
				noDue: "Keine Fälligkeit hinterlegt.",
				gatewayOn: "Die Abbuchung wird automatisch auf der hinterlegten Karte versucht.",
				gatewayOff:
					"Die automatische Zahlung ist hier nicht eingeschaltet: Diese Rechnung wird außerhalb bezahlt und von der Verwaltung dieser Installation als bezahlt markiert.",
				see: "Rechnung ansehen",
			},
			preview: {
				title: "Vorschau der nächsten Rechnung",
				empty: "In diesem Zyklus gibt es bisher nichts zu berechnen.",
				total: "erwartete Summe",
			},
		},

		consumption: {
			from: "von",
			to: "bis",
			allSources: "alle Quellen",
			rangeInverted:
				"Das Enddatum liegt vor dem Startdatum. Die Liste bleibt beim letzten gültigen Zeitraum.",
			sourceName: {
				panel: "Panel",
				channel: "Kanal",
				mission: "Mission",
				automation: "Automatisierung",
			},
			calls: "Antworten",
			credits: "Credits",
			avgDuration: "durchschnittliche Dauer",
			errors: "Fehler",
			when: "wann",
			source: "Quelle",
			agent: "Agent",
			duration: "Dauer",
			state: "Status",
			failed: "Fehler",
			ok: "ok",
			failedDetail: "Diese Antwort ist fehlgeschlagen.",
			turn: "diese Antwort vollständig ansehen",
			noDetail: "Für diese Antwort ist kein Detail hinterlegt.",
			toolsTitle: "In dieser Runde berechnete Werkzeuge",
			toolsLine: "{calls} × {rate} Credits",
			toolsShare: "{n} Credit für Werkzeuge | {n} Credits für Werkzeuge",
			empty: "Keine KI-Antwort in diesem Zeitraum mit diesen Filtern.",
		},

		admin: {
			courtesy: {
				title: "Kulanz",
				endsOn: "Endet mit der Verlängerung am {date}",
				endless: "Kein Enddatum",
				card: "Karte hinterlegt: die erste Abbuchung zum vollen Preis erfolgt am {date}.",
				noCard: "Noch keine Karte: abonniert der Kunde nicht, läuft der Workspace am {date} ab.",
				date: "Kulanz bis",
				dateHint: "Endet mit der Verlängerung an oder nach diesem Datum.",
				save: "Datum speichern",
				clear: "Enddatum entfernen",
				failed: "Das Enddatum der Kulanz konnte nicht gespeichert werden.",
			},
			usage: {
				title: "Tarif und Nutzung",
				subtitle:
					"Was der Workspace nutzt gegenüber dem, was sein Tarif erlaubt – dieselben Zahlen wie in seiner Limits-Ansicht.",
				plan: "Tarif {plan} · {credits} Credits pro Monat",
				none: "Dieser Workspace hat keinen Tarif.",
				unlimited: "unbegrenzt",
			},
			margin: {
				title: "Kosten gegen Erlös, je Arbeitsbereich",
				subtitle:
					"Was jedem Arbeitsbereich berechnet wird, daneben was seine KI dieses Haus in den letzten 30 Tagen gekostet hat.",
				workspace: "Arbeitsbereich",
				plan: "Tarif",
				charged: "Berechnet",
				credits: "Guthaben",
				aiCost: "KI-Kosten (30 T.)",
				noPlan: "kein Tarif",
				empty: "Noch kein Arbeitsbereich zum Vergleichen.",
				note: "KI-Kosten sind nicht die einzigen Kosten, und die Monatsgebühr ist nicht der einzige Erlös — Pakete und Extras bleiben außerhalb dieser Tabelle. Die beiden Spalten stehen zudem in verschiedenen Währungen und werden nie addiert: hier wird kein Wechselkurs geführt. Ein schlechtes Verhältnis ist ein Grund nachzusehen, kein Urteil.",
				error: "Der Vergleich konnte nicht geladen werden. Laden Sie die Seite neu.",
			},
			kpis: {
				workspaces: "Workspaces",
				onRail: "auf der Schiene {rail}",
				invoiced: "berechnet in {month}",
				received: "eingegangen",
				open: "offen",
				overdue: "überfällig",
				note: "Ein gesunder Workspace liegt auf genau einer Schiene — das ist ein Primärschlüssel in der Datenbank, kein Versprechen dieser Maske.",
				rule: "Berechnet = im Monat ausgestellte Rechnungen + im Monat per Karte abgebuchte Guthabenpakete. Eingegangen = was im Monat bezahlt wurde, egal aus welcher Rechnung. Offen und überfällig = alles, was gerade unbezahlt ist. Eine abgelehnte Abbuchung oder eine, die noch auf 3-D Secure wartet, zählt nicht.",
			},
			table: {
				searchPlaceholder: "Workspace suchen",
				allRails: "alle Schienen",
				allPlans: "alle Tarife",
				workspace: "Workspace",
				plan: "Tarif",
				rail: "Schiene",
				nextCharge: "nächste Abbuchung",
				pending: "offen",
				extrasMonthly: "Add-ons/Mon.",
				packs: "Pakete",
				monthlyTotal: "Gesamt/Mon.",
				billedMonth: "berechnet im Monat",
				cardPacks: "{count} per Karte",
				credits: "Credits",
				closeCycle: "Zyklus abschließen",
				empty: "Kein Workspace passt zu diesen Filtern.",
				alreadyClosed:
					"Der Zyklus von {name} war diesen Monat bereits abgeschlossen — es wurde nichts erneut berechnet.",
				closed: "Zyklus von {name} abgeschlossen.",
				closedWithMonth: "Zyklus von {name} abgeschlossen ({month}).",
				closeFailed:
					"Der Zyklus von {name} konnte nicht abgeschlossen werden. Es wurde nichts berechnet.",
				closeFailedWhy:
					"Der Zyklus von {name} konnte nicht abgeschlossen werden. {why} Es wurde nichts berechnet.",
			},
			tenant: {
				title: "Abrechnung",
				subtitle: "Tarif, Credits, Add-ons und die nächste Rechnung",
				none: "Für diesen Workspace ist keine Abrechnung eingerichtet.",
				planCredits: "Tarif-Credits",
				bonus: "Bonus",
				cost: "echte Kosten des Zyklus",
				costHint: "was die Anbieter berechnet haben",
				margin: "Marge",
				marginHint: "wird dem Kunden nie gezeigt",
				plan: "Tarif",
				noPlan: "kein Tarif",
				switchNow: "Jetzt wechseln",
				switchNote:
					"Der Wechsel gilt sofort: Die Credits des neuen Tarifs kommen an und der wiederkehrende Betrag ändert sich.",
				invoices: {
					title: "Zahlungsverlauf",
					subtitle: "jede Rechnung, die dieser Workspace erhalten hat, und wie sie jeweils endete",
					month: "Monat",
					total: "Betrag",
					status: "Status",
					paidAt: "bezahlt am",
					method: "Art",
					externalRef: "externe Referenz",
					/** O meio gravado pelo gateway; um nome desconhecido aparece cru. */
					methods: { stripe: "Karte (Stripe)" },
					none: "Für diesen Workspace wurde noch keine Rechnung ausgestellt.",
				},
			},
			creditActions: {
				title: "Credits, von Hand",
				delta: "Credits (±)",
				deltaPlaceholder: "500",
				pool: "Topf",
				poolPlan: "der Topf des Tarifs (weg beim Wechsel)",
				poolBonus: "Bonus (verfällt nicht)",
				reason: "Grund",
				reasonPlaceholder: "bleibt im Hauptbuch",
				post: "Korrektur buchen",
				needAmount: "Sag, wie viele Credits, mit Vorzeichen — negativ bucht ab.",
				needReason: "Der Grund bleibt im Hauptbuch. Er ist Pflicht.",
				posted: "Korrektur ins Hauptbuch gebucht.",
				adjustFailed:
					"Die Korrektur konnte nicht gebucht werden. Das Guthaben hat sich nicht geändert.",
				blockReason: "Grund für die Sperre",
				needBlockReason:
					"Eine Sperre ohne Grund ist eine Sperre, die später niemand erklären kann.",
				blocked:
					"KI durch Entscheidung der Plattform gesperrt{reason}. Credits aufzuladen hebt das nicht auf.",
				block: "KI sperren",
				unblock: "KI entsperren",
				doneBlocked: "KI für diesen Workspace gesperrt.",
				doneUnblocked: "Sperre aufgehoben.",
				blockFailed: "Die Sperre konnte nicht geändert werden.",
			},
			extras: {
				resource: "Ressource",
				invoiced: "berechnet",
				waived: "erlassen",
				unit: "Einzelpreis",
				subtotal: "Zwischensumme",
				invoicedField: "Berechnetes Add-on für {resource}",
				waivedField: "Erlassenes Add-on für {resource}",
				scheduled: "{resource}: Senkung auf {quantity} für den {date} eingeplant.",
				cancelSchedule: "Einplanung abbrechen",
				save: "Speichern (berechnet anteilig)",
				discard: "Verwerfen",
				notForSale: "nicht im Verkauf",
				perBlock: "je {size}",
				total: "Add-ons gesamt",
				failed: "Die Add-ons konnten nicht gespeichert werden. Es wurde nichts berechnet.",
			},
		},

		pagination: {
			nav: "Seitennummerierung",
			of: "von",
			perPage: "Pro Seite",
			previous: "Zurück",
			next: "Weiter",
			page: "Seite",
			goToPage: "Zur Seite gehen",
			goTo: "Gehe zu",
		},
	},
} as const;
